import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useStadiumStore from './useStadiumStore';
import {
  SECTIONS, ADA_SECTIONS, BOX_DEFS, STAGE, COLORS,
  CANVAS_W, CANVAS_H,
  getZoomTarget, generateRadialSeats,
} from './stadiumData';
import CartSidebar from '../../components/CartSidebar';
import AdminUnsoldAssignModal from '../../components/AdminUnsoldAssignModal';
import api from '../../services/api';
import { isCurrentUserAdmin, isCurrentUserStrictAdmin } from '../../utils/adminCheck';
import { getDayPricingOverrides } from '../../utils/localAdminStore';
import { trackViewContent, trackAddToCart } from '../../utils/metaPixel';
import { applyAmpPricingModelToSeats } from '../../utils/ampPricingModel';
// StadiumDebugger removed for production
import { getCmsImagePath } from '../../cms/cmsData';
import { useCms } from '../../cms/CmsContext';
import { formatArtistNames, getArtistLineup, getLineupArtists } from '../../data/artistLineup';
import './stadium.css';

// ─── Preload stage image for canvas rendering ───
let _stageImg = null;
let _stageImgLoaded = false;
(function preloadStageImg() {
  const img = new Image();
  img.onload = () => { _stageImgLoaded = true; if (window.__stadiumScheduleDraw) window.__stadiumScheduleDraw(); };
  img.src = getCmsImagePath('stagePng', '/stage.webp');
  _stageImg = img;
})();

// ─── Preload handicap symbol SVG for ADA icons ───
let _adaImg = null;
let _adaImgLoaded = false;
(function preloadAdaImg() {
  const img = new Image();
  img.onload = () => { _adaImgLoaded = true; if (window.__stadiumScheduleDraw) window.__stadiumScheduleDraw(); };
  img.src = getCmsImagePath('handicapSymbol', '/handicap-symbol.svg');
  _adaImg = img;
})();

// ─── Seat view images (from stage perspective) ───
const SEAT_VIEW_IMAGES = {
  pit: getCmsImagePath('pitImage', '/pit.webp'),
  circle: getCmsImagePath('circleImage', '/circle.webp'),
  section1: getCmsImagePath('section1Image', '/section1.webp'),
  'section1-ada': getCmsImagePath('section1Image', '/section1.webp'),
  section2: getCmsImagePath('section2Image', '/section2.webp'),
  section3: getCmsImagePath('section3Image', '/section3.webp'),
  'section3-ada-back': getCmsImagePath('section3Image', '/section3.webp'),
  'section3-ada-front': getCmsImagePath('section3Image', '/section3.webp'),
  boxes: getCmsImagePath('boxSeatsImage', '/box-seats-cleaned-web.webp'),
  section4: getCmsImagePath('section4Image', '/section4.webp'),
  section5: getCmsImagePath('section5Image', '/section5.webp'),
  'section5-ada': getCmsImagePath('section5Image', '/section5.webp'),
  section6: getCmsImagePath('section6Image', '/section6.webp'),
  section7: getCmsImagePath('section7Image', '/section7.webp'),
  'section7-ada': getCmsImagePath('section7Image', '/section7.webp'),
  section8: getCmsImagePath('section8Image', '/section8.webp'),
};

function getSeatViewImage(sectionId) {
  return SEAT_VIEW_IMAGES[sectionId] || null;
}

// ═══════════════════════════════════════════════════════════════
//  HELPERS (pure functions, outside component)
// ═══════════════════════════════════════════════════════════════
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawArcShape(ctx, geo, pad = 2) {
  const ir = geo.innerRadius + pad;
  const or = geo.outerRadius - pad;
  ctx.beginPath();
  ctx.arc(geo.cx, geo.cy, or, geo.startAngle, geo.endAngle, false);
  ctx.arc(geo.cx, geo.cy, ir, geo.endAngle, geo.startAngle, true);
  ctx.closePath();
}

function drawCheckmark(ctx, x, y, r) {
  ctx.save();
  ctx.beginPath();
  const s = Math.min(r * 0.32, 2.8);
  ctx.moveTo(x - s * 0.8, y + s * 0.05);
  ctx.lineTo(x - s * 0.15, y + s * 0.65);
  ctx.lineTo(x + s * 0.9, y - s * 0.55);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.min(r * 0.28, 1.8);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.restore();
}

function drawAdaIcon(ctx, x, y, size) {
  if (_adaImgLoaded && _adaImg) {
    ctx.drawImage(_adaImg, x - size / 2, y - size / 2, size, size);
  } else {
    ctx.fillStyle = '#fff';
    ctx.font = `${size}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('\u267F', x, y);
  }
}

function hitTestArc(px, py, geo) {
  const dx = px - geo.cx, dy = py - geo.cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < geo.innerRadius || dist > geo.outerRadius) return false;
  let angle = Math.atan2(dy, dx);
  if (angle < 0) angle += Math.PI * 2;
  let sa = geo.startAngle, ea = geo.endAngle;
  if (sa < 0) { sa += Math.PI * 2; ea += Math.PI * 2; }
  if (angle < sa) angle += Math.PI * 2;
  return angle >= sa && angle <= ea;
}

// ═══════════════════════════════════════════════════════════════
//  DRAW — standalone function called via ref, reads from refs/store
// ═══════════════════════════════════════════════════════════════
let _offCanvas = null;
let _offCtx = null;

function performDraw(canvas, w, h, cam, seatsCache) {
  if (!canvas || !w) return;
  const dpr = window.devicePixelRatio || 1;
  const pw = canvas.width;
  const ph = canvas.height;

  // Double-buffer: draw to offscreen canvas, then blit to visible canvas
  if (!_offCanvas || _offCanvas.width !== pw || _offCanvas.height !== ph) {
    _offCanvas = document.createElement('canvas');
    _offCanvas.width = pw;
    _offCanvas.height = ph;
    _offCtx = _offCanvas.getContext('2d');
  }
  const ctx = _offCtx;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // ── Debug style overrides (per-section individual colors) ──
  const ds = window.__stadiumDebugStyle || null;
  const C = {
    bg: ds?.bg || COLORS.bg,
    stage: ds?.stage || COLORS.stage,
    stageText: ds?.stageText || COLORS.stageText,
    stageGlow: ds?.stageGlow || COLORS.stageGlow,
    selected: ds?.selected || COLORS.selected,
    soldSeat: ds?.soldSeat || 'rgba(255,255,255,0.05)',
  };
  // Per-section color + opacity helper
  const S = (sectionId, prop, fallback) => {
    const key = sectionId + '_' + prop;
    return (ds && ds[key]) ? ds[key] : fallback;
  };
  // Border width helpers (default + hover, controllable per-section)
  const BW = (sectionId) => {
    const key = sectionId + '_borderWidth';
    return (ds && ds[key]) ? parseFloat(ds[key]) : 2;
  };
  const HBW = (sectionId) => {
    const key = sectionId + '_hoverBorderWidth';
    return (ds && ds[key]) ? parseFloat(ds[key]) : 2.5;
  };
  // Global numeric helpers
  const N = (prop, fallback) => (ds && ds[prop]) ? parseFloat(ds[prop]) : fallback;
  const G = (prop, fallback) => (ds && ds[prop]) ? ds[prop] : fallback;
  // Per-ADA band helper
  const A = (adaId, prop, fallback) => {
    const key = adaId + '_' + prop;
    return (ds && ds[key]) ? ds[key] : fallback;
  };

  // ── Background ──
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(cam.x, cam.y);
  ctx.scale(cam.scale, cam.scale);

  // ── Venue glow (subtle, matches light bg) ──
  const glowGrad = ctx.createRadialGradient(700, 540, 40, 700, 420, 680);
  glowGrad.addColorStop(0, 'rgba(99, 102, 241, 0.03)');
  glowGrad.addColorStop(0.5, 'rgba(99, 102, 241, 0.01)');
  glowGrad.addColorStop(1, 'transparent');
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, 0, 1400, 900);

  // ── Stage (scaled via debugger stageScale %) ──
  const stageScalePct = N('stageScale', 100) / 100;
  const stW = STAGE.width * stageScalePct;
  const stH = STAGE.height * stageScalePct;
  const stX = STAGE.x + (STAGE.width - stW) / 2;
  const stY = STAGE.y + (STAGE.height - stH) / 2;

  if (_stageImgLoaded && _stageImg) {
    const imgAspect = _stageImg.width / _stageImg.height;
    const drawW = stW * 1.15;
    const drawH = drawW / imgAspect;
    const drawX = stX + (stW - drawW) / 2;
    const drawY = stY + (stH - drawH) / 2;
    ctx.save();
    ctx.shadowColor = C.stageGlow + '60';
    ctx.shadowBlur = 40;
    ctx.drawImage(_stageImg, drawX, drawY, drawW, drawH);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.restore();
  } else {
    // Fallback: draw rectangle if image not yet loaded
    roundRect(ctx, stX, stY, stW, stH, STAGE.radius * stageScalePct);
    ctx.shadowColor = C.stageGlow + '60';
    ctx.shadowBlur = 40;
    ctx.fillStyle = C.stage;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.strokeStyle = C.stageGlow + '30';
    ctx.lineWidth = N('stageBorderWidth', 1);
    ctx.stroke();
    ctx.fillStyle = C.stageText;
    const fontSize = Math.round(18 * stageScalePct);
    ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(STAGE.label, stX + stW / 2, stY + stH / 2);
  }

  // ── Sections ──
  const state = useStadiumStore.getState();
  // showSeats = user clicked a section; isZoomed = camera zoomed past threshold
  const fitScale = Math.min(w / CANVAS_W, h / CANVAS_H) * 0.92;
  const showSeats = state.viewMode === 'section';
  const isZoomed = showSeats || cam.scale > fitScale * 1.3;

  // Admins can flip off the sold-out greying to inspect seats/prices/buyers.
  const bypassSoldOut = !!state.adminBypassSoldOut;
  const soldOutBySection = {};
  SECTIONS.forEach((section) => {
    const parentSeats = seatsCache[section.id] || [];
    const childAdaSeats = ADA_SECTIONS
      .filter((ada) => ada.parentId === section.id)
      .flatMap((ada) => seatsCache[ada.id] || []);
    const allSeats = [...parentSeats, ...childAdaSeats];

    soldOutBySection[section.id] = !bypassSoldOut && allSeats.length > 0 && allSeats.every((seat) => {
      const seatState = state.seatMap[seat.id] || seat;
      return seatState.status === 'sold' || seatState.status === 'held';
    });
  });

  // Build lookup of ADA band radial ranges per parent section (to skip overlapping seats)
  const adaRadiiForSection = {};
  ADA_SECTIONS.forEach(ada => {
    const pid = ada.parentId;
    if (!adaRadiiForSection[pid]) adaRadiiForSection[pid] = [];
    adaRadiiForSection[pid].push({
      innerR: ada.geo.innerRadius - 2,
      outerR: ada.geo.outerRadius + 2,
      startA: ada.geo.startAngle,
      endA: ada.geo.endAngle,
    });
  });

  SECTIONS.forEach(section => {
    const isHovered = state.hoveredSection === section.id;
    const isActive = state.activeSection === section.id;
    const sectionIsSoldOut = !!soldOutBySection[section.id];

    ctx.save();
    drawArcShape(ctx, section.geo, 2);

    const sid = section.id;
    if (sectionIsSoldOut) {
      ctx.fillStyle = 'rgba(155,155,165,0.22)';
      ctx.fill();
      drawArcShape(ctx, section.geo, 2);
      ctx.strokeStyle = 'rgba(120,120,130,0.55)';
      ctx.lineWidth = BW(sid);
      ctx.stroke();
    } else if (showSeats && !isActive) {
      // Grey out non-active sections when a section is selected
      ctx.fillStyle = 'rgba(180,180,190,0.18)';
      ctx.fill();
      drawArcShape(ctx, section.geo, 2);
      ctx.strokeStyle = 'rgba(160,160,170,0.45)';
      ctx.lineWidth = BW(sid);
      ctx.stroke();
    } else if (isZoomed && !isActive) {
      // Zoomed via +/- but no section selected — normal appearance
      ctx.fillStyle = S(sid, 'fill', section.color) + S(sid, 'fillOp', '40');
      ctx.fill();
      drawArcShape(ctx, section.geo, 2);
      ctx.strokeStyle = S(sid, 'border', section.color) + S(sid, 'borderOp', 'ff');
      ctx.lineWidth = BW(sid);
      ctx.stroke();
    } else if (isZoomed && isActive) {
      ctx.fillStyle = S(sid, 'zoomedFill', section.color) + S(sid, 'zoomedFillOp', '22');
      ctx.fill();
      // Full closed shape stroke — all 4 sides, FULLY OPAQUE
      drawArcShape(ctx, section.geo, 2);
      ctx.strokeStyle = S(sid, 'zoomedBorder', section.color) + S(sid, 'zoomedBorderOp', 'ff');
      ctx.lineWidth = BW(sid);
      ctx.stroke();
    } else {
      // Overview: normal vs hovered
      if (isHovered) {
        ctx.fillStyle = S(sid, 'hoverFill', section.color) + S(sid, 'hoverFillOp', '60');
        ctx.fill();
        // FULLY OPAQUE stroke so border is single color
        drawArcShape(ctx, section.geo, 2);
        ctx.strokeStyle = S(sid, 'hoverBorder', section.color) + S(sid, 'hoverBorderOp', 'ff');
        ctx.lineWidth = HBW(sid);
        ctx.stroke();
        ctx.shadowColor = S(sid, 'hoverFill', section.color) + '50';
        ctx.shadowBlur = N('hoverGlowBlur', 35);
        ctx.stroke();
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      } else {
        ctx.fillStyle = S(sid, 'fill', section.color) + S(sid, 'fillOp', '40');
        ctx.fill();
        // FULLY OPAQUE stroke so border is single color
        drawArcShape(ctx, section.geo, 2);
        ctx.strokeStyle = S(sid, 'border', section.color) + S(sid, 'borderOp', 'ff');
        ctx.lineWidth = BW(sid);
        ctx.stroke();
      }
    }
    ctx.restore();

    // Row lines (overview + non-active sections when zoomed, hidden when greyed out)
    if ((!isZoomed || (isZoomed && !isActive)) && !(showSeats && !isActive) && !sectionIsSoldOut) {
      const g = section.geo;
      const nRows = section.rows.length;
      ctx.save();
      ctx.globalAlpha = isHovered ? N('rowLineHoverOpacity', 0.22) : N('rowLineOpacity', 0.08);
      for (let r = 1; r < nRows; r++) {
        const t = r / nRows;
        const rad = g.innerRadius + t * (g.outerRadius - g.innerRadius);
        ctx.beginPath();
        ctx.arc(g.cx, g.cy, rad, g.startAngle + 0.015, g.endAngle - 0.015, false);
        ctx.strokeStyle = S(sid, 'fill', section.color);
        ctx.lineWidth = 0.7;
        ctx.stroke();
      }
      ctx.restore();
    }

    // Labels (overview + non-active sections, including greyed-out sections)
    if ((!isZoomed || (isZoomed && !isActive)) && section.labelPos) {
      const isGreyed = sectionIsSoldOut || (showSeats && !isActive);
      ctx.save();
      if (isGreyed) ctx.globalAlpha = 0.45;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const lx = section.labelPos.x, ly = section.labelPos.y;

      ctx.fillStyle = isGreyed ? '#666666' : G('labelColor', '#111111');
      ctx.font = '700 12px Inter, system-ui, sans-serif';
      ctx.fillText(section.id === 'boxes' ? section.label : section.shortLabel, lx, ly - 10);

      // Price pill
      const pt = sectionIsSoldOut ? 'SOLD OUT' : section.displayPrice;
      const tw = ctx.measureText(pt).width + 14;
      roundRect(ctx, lx - tw / 2, ly + 2, tw, 20, 10);
      ctx.fillStyle = isGreyed ? 'rgba(0,0,0,0.35)' : G('pricePillBg', 'rgba(0,0,0,0.5)');
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetY = 2;
      ctx.fill();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      ctx.fillStyle = isGreyed ? 'rgba(255,255,255,0.8)' : G('priceColor', '#ffffff');
      ctx.font = '700 11px Inter, system-ui, sans-serif';
      ctx.fillText(pt, lx, ly + 12);
      ctx.restore();

      // B4 ADA icon in overview (dimmed when greyed out)
      if (section.isBoxSection) {
        const b4 = BOX_DEFS.find(b => b.rows.some(r => r.isADA));
        if (b4?.geo) {
          const midA4 = (b4.geo.startAngle + b4.geo.endAngle) / 2;
          const adaR4 = b4.geo.outerRadius + 3;
          const ax4 = b4.geo.cx + Math.cos(midA4) * adaR4;
          const ay4 = b4.geo.cy + Math.sin(midA4) * adaR4 - 3;
          ctx.save();
          if (isGreyed) ctx.globalAlpha = 0.35;
          ctx.beginPath();
          ctx.arc(ax4, ay4, 9, 0, Math.PI * 2);
          ctx.fillStyle = A('ada', 'iconCircle', COLORS.ada) + A('ada', 'iconCircleOp', '70');
          ctx.fill();
          drawAdaIcon(ctx, ax4, ay4, 9 * 1.4);
          ctx.restore();
        }
      }
    }

    // Section labels (zoomed) — small text just inside the inner arc edge with
    // enough padding that the text doesn't touch the curve or any seats.
    if (showSeats && isZoomed && section.labelPos && !sectionIsSoldOut) {
      const g = section.geo;
      const midA = (g.startAngle + g.endAngle) / 2;
      const needsOffset = ['section1', 'section3', 'section4', 'section5', 'section7', 'section8'].includes(section.id);
      const labelR = needsOffset ? g.innerRadius - 18 : g.innerRadius - 8;
      const lx = g.cx + Math.cos(midA) * labelR;
      const ly = g.cy + Math.sin(midA) * labelR;
      ctx.save();
      ctx.fillStyle = G('zoomedLabelColor', '#111111');
      ctx.font = '600 8px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(section.shortLabel, lx, ly);
      ctx.restore();
    }

    // Individual seats (only when user clicked a section)
    if (showSeats && isActive) {
      const seats = seatsCache[section.id] || [];

      // ── Box section: draw sub-box arcs, labels, then seats ──
      if (section.isBoxSection) {
        const hovBoxId = state.hoveredSeat
          ? (seats.find(s => s.id === state.hoveredSeat)?.boxId || null)
          : null;

        BOX_DEFS.forEach(box => {
          const bg = box.geo;
          // Draw sub-box arc
          ctx.save();
          drawArcShape(ctx, bg, 1);
          const boxSeats = seats.filter(s => s.boxId === box.id);
          const anySelected = boxSeats.some((s) => {
            const seatStatus = (state.seatMap[s.id] || s).status;
            return seatStatus === 'selected' || isAssignableSelectedStatus(seatStatus);
          });
          const isHovBox = hovBoxId === box.id;

          if (anySelected) {
            ctx.fillStyle = C.selected + '30';
            ctx.fill();
            ctx.strokeStyle = C.selected + '90';
            ctx.lineWidth = 2;
            ctx.stroke();
          } else if (isHovBox) {
            ctx.fillStyle = S(sid, 'hoverFill', section.color) + '50';
            ctx.fill();
            ctx.strokeStyle = S(sid, 'hoverBorder', section.color) + 'cc';
            ctx.lineWidth = 2;
            ctx.stroke();
          } else {
            ctx.fillStyle = S(sid, 'zoomedFill', section.color) + '18';
            ctx.fill();
            ctx.strokeStyle = S(sid, 'zoomedBorder', section.color) + '50';
            ctx.lineWidth = 1;
            ctx.stroke();
          }
          ctx.restore();

          // Box label
          const midA = (bg.startAngle + bg.endAngle) / 2;
          const lx = bg.cx + Math.cos(midA) * (bg.innerRadius - 12);
          const ly = bg.cy + Math.sin(midA) * (bg.innerRadius - 12);
          ctx.save();
          ctx.fillStyle = G('boxLabelColor', '#111111');
          ctx.font = '600 7px Inter, system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(box.id, lx, ly);
          ctx.restore();

          // ADA icon for B4 Row C (above the box label, towards outer edge)
          if (box.rows.some(r => r.isADA)) {
            const adaR = bg.outerRadius + 3;
            const ax = bg.cx + Math.cos(midA) * adaR;
            const ay = bg.cy + Math.sin(midA) * adaR - 3;
            ctx.beginPath();
            ctx.arc(ax, ay, 6, 0, Math.PI * 2);
            ctx.fillStyle = A('ada', 'iconCircle', COLORS.ada) + A('ada', 'iconCircleOp', '70');
            ctx.fill();
            drawAdaIcon(ctx, ax, ay, 8);
          }
        });

        // Draw box seats
        seats.forEach(seat => {
          const ss = state.seatMap[seat.id] || seat;
          const assignableState = parseAssignableStatus(ss.status);
          const assignable = !!assignableState;
          const assignableSelected = !!(assignableState && assignableState.selected);
          const poolStyle = getAssignablePoolStyle(assignableState?.poolKey);
          const sel = ss.status === 'selected' || assignableSelected;
          const sold = ss.status === 'sold' || ss.status === 'held';
          const isHovBox = hovBoxId === seat.boxId;

          ctx.beginPath();
          const r = isHovBox ? seat.radius * 1.3 : seat.radius;
          ctx.arc(seat.x, seat.y, r, 0, Math.PI * 2);

          if (sel) {
            ctx.fillStyle = assignableSelected ? poolStyle.hoverColor : C.selected;
            ctx.fill();
            drawCheckmark(ctx, seat.x, seat.y, seat.radius);
          } else if (sold) {
            ctx.fillStyle = S(sid, 'soldSeat', C.soldSeat);
            ctx.fill();
          } else if (assignable) {
            ctx.fillStyle = isHovBox ? poolStyle.hoverColor : poolStyle.color;
            ctx.fill();
          } else if (seat.isADA) {
            ctx.fillStyle = isHovBox
              ? (A('ada', 'seatHover', COLORS.ada) + A('ada', 'seatHoverOp', 'cc'))
              : (A('ada', 'seatFill', COLORS.ada) + A('ada', 'seatOp', '90'));
            ctx.fill();
          } else {
            ctx.fillStyle = S(sid, isHovBox ? 'seatHover' : 'seat', section.color) + S(sid, isHovBox ? 'seatHoverOp' : 'seatOp', isHovBox ? 'dd' : '99');
            ctx.fill();
          }
        });
      } else {
        // ── Normal section seat rendering ──
        const adaBands = adaRadiiForSection[section.id];
        seats.forEach(seat => {
          // Check if seat physically overlaps any child ADA band (radial + angular)
          let inAdaBand = false;
          if (adaBands) {
            const dx = seat.x - section.geo.cx, dy = seat.y - section.geo.cy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const angle = Math.atan2(dy, dx);
            for (const band of adaBands) {
              if (dist >= band.innerR && dist <= band.outerR) {
                // Normalize angle into [0, 2π)
                let a = angle, sa = band.startA, ea = band.endA;
                while (a < 0) a += Math.PI * 2;
                while (sa < 0) sa += Math.PI * 2;
                while (ea < 0) ea += Math.PI * 2;
                const inArc = sa <= ea ? (a >= sa && a <= ea) : (a >= sa || a <= ea);
                if (inArc) { inAdaBand = true; break; }
              }
            }
          }
          const ss = state.seatMap[seat.id] || seat;
          const hov = state.hoveredSeat === seat.id;
          const assignableState = parseAssignableStatus(ss.status);
          const assignable = !!assignableState;
          const assignableSelected = !!(assignableState && assignableState.selected);
          const poolStyle = getAssignablePoolStyle(assignableState?.poolKey);
          const sel = ss.status === 'selected' || assignableSelected;
          const sold = ss.status === 'sold' || ss.status === 'held';

          ctx.beginPath();
          const r = hov ? seat.radius * 1.4 : seat.radius;
          ctx.arc(seat.x, seat.y, r, 0, Math.PI * 2);

          if (sel) {
            ctx.fillStyle = assignableSelected ? poolStyle.hoverColor : C.selected;
            ctx.fill();
            drawCheckmark(ctx, seat.x, seat.y, seat.radius);
          } else if (sold) {
            ctx.fillStyle = S(sid, 'soldSeat', C.soldSeat);
            ctx.fill();
          } else if (assignable) {
            ctx.fillStyle = hov ? poolStyle.hoverColor : poolStyle.color;
            ctx.fill();
          } else if (inAdaBand) {
            ctx.fillStyle = hov
              ? (A('ada', 'seatHover', COLORS.ada) + A('ada', 'seatHoverOp', 'cc'))
              : (A('ada', 'seatFill', COLORS.ada) + A('ada', 'seatOp', '90'));
            ctx.fill();
          } else if (hov) {
            ctx.fillStyle = S(sid, 'seatHover', section.color) + S(sid, 'seatHoverOp', 'dd');
            ctx.fill();
          } else {
            ctx.fillStyle = S(sid, 'seat', section.color) + S(sid, 'seatOp', '99');
            ctx.fill();
          }
        });
      }

      // ── Row labels for active section (non-box) ──
      if (!section.isBoxSection && section.rows) {
        const g = section.geo;
        const totalRows = section.rows.length;
        const radPad = 6;
        const innerR = g.innerRadius + radPad;
        const outerR = g.outerRadius - radPad;
        const depth = outerR - innerR;
        const rowHeight = totalRows > 1 ? depth / (totalRows - 1) : depth;
        const fontSize = Math.max(2.5, Math.min(rowHeight * 0.38, 5));

        ctx.save();
        ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const labelAngle = g.startAngle - 0.025;

        for (let r = 0; r < totalRows; r++) {
          const t = totalRows === 1 ? 0.5 : r / (totalRows - 1);
          const radius = innerR + t * depth;
          ctx.fillText(section.rows[r], g.cx + Math.cos(labelAngle) * radius, g.cy + Math.sin(labelAngle) * radius);
        }

        ctx.restore();
      }
    }
  });

  // ── ADA Bands ──
  ADA_SECTIONS.forEach(ada => {
    const adaHov = !isZoomed && state.hoveredSection === ada.id;
    const parentSoldOut = !!soldOutBySection[ada.parentId];
    ctx.save();
    drawArcShape(ctx, ada.geo, 0);

    // First: paint opaque background to cover any parent section fill underneath
    ctx.fillStyle = C.bg;
    ctx.fill();

    const adaBW = parseFloat(A('ada', 'borderWidth', '1')) || 1;
    const adaHBW = parseFloat(A('ada', 'hoverBorderWidth', '1.5')) || 1.5;

    const adaIsActive = state.activeSection && ada.parentId === state.activeSection;
    const adaGreyedOut = parentSoldOut || (showSeats && !adaIsActive);
    if (adaGreyedOut) {
      // Grey out ADA bands when viewing a different section
      drawArcShape(ctx, ada.geo, 0);
      ctx.fillStyle = 'rgba(180,180,190,0.12)';
      ctx.fill();
      drawArcShape(ctx, ada.geo, 0);
      ctx.strokeStyle = 'rgba(160,160,170,0.35)';
      ctx.lineWidth = adaBW;
      ctx.stroke();
    } else if (isZoomed && adaIsActive) {
      drawArcShape(ctx, ada.geo, 0);
      ctx.fillStyle = A('ada', 'zoomedFill', COLORS.ada) + A('ada', 'zoomedFillOp', '30');
      ctx.fill();
      drawArcShape(ctx, ada.geo, 0);
      ctx.strokeStyle = A('ada', 'zoomedBorder', COLORS.ada) + A('ada', 'zoomedBorderOp', 'ff');
      ctx.lineWidth = adaBW;
      ctx.stroke();
    } else if (isZoomed && !adaIsActive) {
      // Keep normal overview appearance for non-active ADA bands
      drawArcShape(ctx, ada.geo, 0);
      ctx.fillStyle = A('ada', 'fill', COLORS.ada) + A('ada', 'fillOp', '25');
      ctx.fill();
      drawArcShape(ctx, ada.geo, 0);
      ctx.strokeStyle = A('ada', 'border', COLORS.ada) + A('ada', 'borderOp', 'ff');
      ctx.lineWidth = adaBW;
      ctx.stroke();
    } else if (adaHov) {
      drawArcShape(ctx, ada.geo, 0);
      ctx.fillStyle = A('ada', 'hoverFill', COLORS.ada) + A('ada', 'hoverFillOp', '50');
      ctx.fill();
      drawArcShape(ctx, ada.geo, 0);
      ctx.strokeStyle = A('ada', 'hoverBorder', COLORS.ada) + A('ada', 'hoverBorderOp', 'cc');
      ctx.lineWidth = adaHBW;
      ctx.stroke();
      ctx.shadowColor = A('ada', 'hoverFill', COLORS.ada) + '50';
      ctx.shadowBlur = N('hoverGlowBlur', 35);
      ctx.stroke();
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    } else {
      drawArcShape(ctx, ada.geo, 0);
      ctx.fillStyle = A('ada', 'fill', COLORS.ada) + A('ada', 'fillOp', '25');
      ctx.fill();
      drawArcShape(ctx, ada.geo, 0);
      ctx.strokeStyle = A('ada', 'border', COLORS.ada) + A('ada', 'borderOp', 'ff');
      ctx.lineWidth = adaBW;
      ctx.stroke();
    }
    ctx.restore();

    // ADA icon — placed OUTSIDE the seating area (dimmed when greyed out)
    {
      const g = ada.geo;
      const iconR = 9;
      const midA = (g.startAngle + g.endAngle) / 2;
      const isFrontBand = ada.id.includes('front');
      const iconDist = isFrontBand
        ? (g.innerRadius - iconR - 2)
        : (g.outerRadius + iconR + 4);
      const ix = g.cx + Math.cos(midA) * iconDist;
      const iy = g.cy + Math.sin(midA) * iconDist;

      ctx.save();
      if (adaGreyedOut) ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.arc(ix, iy, iconR, 0, Math.PI * 2);
      ctx.fillStyle = A('ada', 'iconCircle', COLORS.ada) + A('ada', 'iconCircleOp', '70');
      ctx.fill();

      drawAdaIcon(ctx, ix, iy, iconR * 1.4);
      ctx.restore();
    }

    // ADA seats — only when user clicked a section
    if (showSeats && state.activeSection && ada.parentId === state.activeSection) {
      const seats = seatsCache[ada.id] || [];
      seats.forEach(seat => {
        const ss = state.seatMap[seat.id] || seat;
        const hov = state.hoveredSeat === seat.id;
        const assignableState = parseAssignableStatus(ss.status);
        const assignable = !!assignableState;
        const assignableSelected = !!(assignableState && assignableState.selected);
        const poolStyle = getAssignablePoolStyle(assignableState?.poolKey);
        const sel = ss.status === 'selected' || assignableSelected;
        const sold = ss.status === 'sold' || ss.status === 'held';

        ctx.beginPath();
        const r = hov ? seat.radius * 1.4 : seat.radius;
        ctx.arc(seat.x, seat.y, r, 0, Math.PI * 2);

        if (sel) {
          ctx.fillStyle = assignableSelected ? poolStyle.hoverColor : C.selected;
          ctx.fill();
          drawCheckmark(ctx, seat.x, seat.y, seat.radius);
        } else if (sold) {
          ctx.fillStyle = A('ada', 'soldSeat', C.soldSeat);
          ctx.fill();
        } else if (assignable) {
          ctx.fillStyle = hov ? poolStyle.hoverColor : poolStyle.color;
          ctx.fill();
        } else if (hov) {
          ctx.fillStyle = A('ada', 'seatHover', COLORS.ada) + A('ada', 'seatHoverOp', 'cc');
          ctx.fill();
        } else {
          ctx.fillStyle = A('ada', 'seatFill', COLORS.ada) + A('ada', 'seatOp', '90');
          ctx.fill();
        }
      });

      // ── ADA row labels ──
      if (ada.rows) {
        const g = ada.geo;
        const totalRows = ada.rows.length;
        const radPad = 1;
        const innerR = g.innerRadius + radPad;
        const outerR = g.outerRadius - radPad;
        const depth = outerR - innerR;
        const fontSize = Math.max(2.5, Math.min(depth * 0.5, 4.5));

        ctx.save();
        ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = 'rgba(59,130,246,0.7)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const labelAngle = g.startAngle - 0.02;

        for (let r = 0; r < totalRows; r++) {
          const t = totalRows === 1 ? 0.5 : r / (totalRows - 1);
          const radius = innerR + t * depth;
          ctx.fillText(ada.rows[r], g.cx + Math.cos(labelAngle) * radius, g.cy + Math.sin(labelAngle) * radius);
        }

        ctx.restore();
      }
    }
  });

  ctx.restore();

  // Blit completed frame to visible canvas in one shot (no tearing)
  const mainCtx = canvas.getContext('2d');
  mainCtx.setTransform(1, 0, 0, 1, 0, 0);
  mainCtx.clearRect(0, 0, pw, ph);
  mainCtx.drawImage(_offCanvas, 0, 0);
}

// ═══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const ALLOWED_EMAILS = ['moe@ocmenafestival.com', 'admin@ocmena.com', 'sam@ocmenafestival.com', 'omar@ocmenafestival.com'];

// eslint-disable-next-line no-unused-vars
function StadiumAuthGate() {
  const { user, isAuthenticated, login, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [restored, setRestored] = useState(false);

  // Restore persistent stadium session from localStorage (keeps allowed users logged in forever)
  useEffect(() => {
    const tryRestore = async () => {
      if (!sessionStorage.getItem('accessToken') && localStorage.getItem('stadium_accessToken')) {
        // Copy tokens to sessionStorage temporarily so api.request can use them
        const access = localStorage.getItem('stadium_accessToken');
        const refresh = localStorage.getItem('stadium_refreshToken');
        const usr = localStorage.getItem('stadium_user');
        sessionStorage.setItem('accessToken', access);
        sessionStorage.setItem('refreshToken', refresh);
        sessionStorage.setItem('user', usr);
        try {
          // Validate tokens by calling the profile endpoint
          const res = await api.getProfile();
          if (res?.success) {
            // Tokens validated (may have been refreshed) — persist fresh tokens
            localStorage.setItem('stadium_accessToken', sessionStorage.getItem('accessToken'));
            localStorage.setItem('stadium_refreshToken', sessionStorage.getItem('refreshToken'));
            localStorage.setItem('stadium_user', sessionStorage.getItem('user'));
            // Reload to let AuthProvider pick them up
            window.location.reload();
            return;
          }
        } catch { /* token expired */ }
        // Tokens invalid — clean up everywhere
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('user');
        localStorage.removeItem('stadium_accessToken');
        localStorage.removeItem('stadium_refreshToken');
        localStorage.removeItem('stadium_user');
      }
      setRestored(true);
    };
    tryRestore();
  }, []);

  // Skip auth on localhost / LAN (development)
  const host = window.location.hostname;
  const isDev = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.') || host.startsWith('10.');
  if (isDev) return <StadiumPageInner />;
  if (!restored) return null;

  if (authLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f111e', color: '#fff', fontFamily: 'Inter, system-ui, sans-serif' }}>Loading...</div>;

  const allowed = isAuthenticated && user?.email && ALLOWED_EMAILS.includes(user.email.toLowerCase());
  if (allowed) return <StadiumPageInner />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoggingIn(true);
    try {
      const res = await login(email, password);
      if (!res?.success) {
        setError(res?.message || 'Invalid credentials');
      } else if (!ALLOWED_EMAILS.includes(email.toLowerCase())) {
        setError('You do not have access to this page');
      } else {
        // Persist session for allowed emails (never expire)
        localStorage.setItem('stadium_accessToken', sessionStorage.getItem('accessToken'));
        localStorage.setItem('stadium_refreshToken', sessionStorage.getItem('refreshToken'));
        localStorage.setItem('stadium_user', sessionStorage.getItem('user'));
      }
    } catch {
      setError('Login failed');
    }
    setLoggingIn(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f111e', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <form onSubmit={handleLogin} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '40px 32px', width: 340, border: '1px solid rgba(255,255,255,0.1)' }}>
        <h2 style={{ color: '#fff', margin: '0 0 8px', fontSize: 22, fontWeight: 700 }}>Stadium Access</h2>
        <p style={{ color: '#9ca3af', margin: '0 0 24px', fontSize: 13 }}>Authorized personnel only</p>
        {isAuthenticated && !allowed && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#f87171', fontSize: 12 }}>Your account does not have access to this page.</div>}
        {error && <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#f87171', fontSize: 12 }}>{error}</div>}
        <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, marginBottom: 12, boxSizing: 'border-box', outline: 'none' }} />
        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, marginBottom: 20, boxSizing: 'border-box', outline: 'none' }} />
        <button type="submit" disabled={loggingIn} style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: '#6366f1', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: loggingIn ? 0.6 : 1 }}>{loggingIn ? 'Signing in...' : 'Sign In'}</button>
      </form>
    </div>
  );
}

// Auth gate disabled for public launch — export inner component directly
// export default StadiumAuthGate;
export default function StadiumPage(props) {
  return <StadiumPageInner {...props} />;
}

// ── Day constants (static, outside component to avoid lint dep warnings) ──
const DAY_TO_EVENT_DATE = { saturday: '2026-06-20', sunday: '2026-06-21' };
const DAY_TO_JSON = { saturday: '/saturday.json', sunday: '/sunday.json' };
const BLOCKED_POOL_OWNER_EMAIL = 'samaresheh@gmail.com';
const ADMIN_ASSIGNABLE_STATUS = 'admin-assignable';
const ADMIN_ASSIGNABLE_SELECTED_STATUS = 'admin-assignable-selected';
const ADMIN_ASSIGNABLE_DEFAULT_POOL_STYLE = { color: '#22c55e', hoverColor: '#16a34a', label: 'Shared Pool' };
const ADMIN_ASSIGNABLE_POOL_STYLES = {
  shared: { color: '#22c55e', hoverColor: '#16a34a', label: 'Shared Pool · samaresheh@gmail.com' },
  sam_private: { color: '#f59e0b', hoverColor: '#d97706', label: 'Sam Private · sam@ocmenafestival.com' },
  omar_private: { color: '#0ea5e9', hoverColor: '#0284c7', label: 'Omar Private · omar@ocmenafestival.com' },
};

function makeAssignableStatus(poolKey, selected = false) {
  const normalized = String(poolKey || 'shared').trim().toLowerCase() || 'shared';
  return `${selected ? ADMIN_ASSIGNABLE_SELECTED_STATUS : ADMIN_ASSIGNABLE_STATUS}:${normalized}`;
}

function parseAssignableStatus(status) {
  const raw = String(status || '').trim().toLowerCase();
  if (!raw) return null;
  const selectedPrefix = `${ADMIN_ASSIGNABLE_SELECTED_STATUS}:`;
  const regularPrefix = `${ADMIN_ASSIGNABLE_STATUS}:`;
  if (raw.startsWith(selectedPrefix)) {
    return { selected: true, poolKey: raw.slice(selectedPrefix.length) || 'shared' };
  }
  if (raw.startsWith(regularPrefix)) {
    return { selected: false, poolKey: raw.slice(regularPrefix.length) || 'shared' };
  }
  return null;
}

function isAssignableStatus(status) {
  return !!parseAssignableStatus(status);
}

function isAssignableSelectedStatus(status) {
  const parsed = parseAssignableStatus(status);
  return !!(parsed && parsed.selected);
}

function getAssignablePoolStyle(poolKey) {
  const normalized = String(poolKey || '').trim().toLowerCase();
  return ADMIN_ASSIGNABLE_POOL_STYLES[normalized] || ADMIN_ASSIGNABLE_DEFAULT_POOL_STYLE;
}

const API_TO_STADIUM = {
  'pit-x1': 'pit', 'circle-all': 'circle',
  'orch-1': 'section1', 'orch-2': 'section2', 'orch-3': 'section3',
  'terr-1': 'section8', 'terr-4': 'section4', 'terr-5': 'section5',
  'terr-6': 'section6', 'terr-7': 'section7',
};
const ADA_TO_STADIUM = {
  'orch-1-ada': 'section1-ada',
  'orch-3-ada': 'section3-ada-back',
  'terr-5-ada': 'section5-ada',
  'terr-7-ada': 'section7-ada',
};
const ADA_ROWS = {
  'section1': { 'Z': 'section1-ada' },
  'section3': { 'Z': 'section3-ada-back', 'B': 'section3-ada-front' },
  'section5': { 'Z': 'section5-ada' },
  'section7': { 'Z': 'section7-ada' },
};

// Reverse mapping: stadium section IDs → API section IDs (for hold requests)
const STADIUM_TO_API = {
  'pit': 'pit-x1', 'circle': 'circle-all',
  'section1': 'orch-1', 'section2': 'orch-2', 'section3': 'orch-3',
  'section4': 'terr-4', 'section5': 'terr-5', 'section6': 'terr-6',
  'section7': 'terr-7', 'section8': 'terr-1',
  'section1-ada': 'orch-1-ada', 'section3-ada-back': 'orch-3-ada',
  'section3-ada-front': 'orch-3-ada', 'section5-ada': 'terr-5-ada',
  'section7-ada': 'terr-7-ada',
  'boxes': null, // boxes use box-1 through box-8
};

const BLOCKED_SECTION_TO_STADIUM = {
  'Pit': 'pit',
  'Circle': 'circle',
  'Section 1': 'section1',
  'Section 2': 'section2',
  'Section 3': 'section3',
  'Section 4': 'section4',
  'Section 5': 'section5',
  'Section 6': 'section6',
  'Section 7': 'section7',
  'Section 8': 'section8',
  'Orchestra 1': 'section1',
  'Orchestra 2': 'section2',
  'Orchestra 3': 'section3',
  'Terrace 1': 'section8',
  'Terrace 4': 'section4',
  'Terrace 5': 'section5',
  'Terrace 6': 'section6',
  'Terrace 7': 'section7',
};

const FRONTEND_ROW_LABELS_BY_SECTION = {
  Pit: ['AAA', 'BBB', 'CCC', 'DDD', 'EEE', 'FFF', 'GGG'],
  Circle: ['AA', 'BB', 'CC', 'DD', 'EE'],
};

function normalizeBlockedSeatRow(sectionLabel, rowValue) {
  const normalized = String(rowValue || '').trim().toUpperCase();
  if (!normalized) return '';
  const frontendRows = FRONTEND_ROW_LABELS_BY_SECTION[sectionLabel];
  if (!frontendRows || normalized.length !== 1) return normalized;
  const rowIndex = normalized.charCodeAt(0) - 65;
  return frontendRows[rowIndex] || normalized;
}

function mapBlockedSeatToStadiumSeatId(blockedSeat) {
  const sectionLabel = String(blockedSeat?.section_display || blockedSeat?.section || '').trim();
  const row = normalizeBlockedSeatRow(sectionLabel, blockedSeat?.row);
  const seatNumber = parseInt(blockedSeat?.seat_number, 10);
  if (!row || Number.isNaN(seatNumber) || !sectionLabel) return '';

  const boxMatch = sectionLabel.match(/^Box(?: Seat)?\s*(\d+)$/i);
  if (boxMatch) {
    return `boxes-B${boxMatch[1]}-${row}-${seatNumber}`;
  }

  const stadiumSid = BLOCKED_SECTION_TO_STADIUM[sectionLabel];
  if (!stadiumSid) return '';

  const adaMap = ADA_ROWS[stadiumSid];
  const ADA_MAX_SEAT = { 'section1-ada': 28, 'section3-ada-back': 28, 'section3-ada-front': 14 };
  let targetSid = stadiumSid;
  if (adaMap && adaMap[row]) {
    const adaSid = adaMap[row];
    const maxSeat = ADA_MAX_SEAT[adaSid];
    targetSid = (!maxSeat || seatNumber <= maxSeat) ? adaSid : stadiumSid;
  }

  return `${targetSid}-${row}-${seatNumber}`;
}

// Session key for seat holds (persistent per browser tab)
function getStadiumSessionKey() {
  let key = sessionStorage.getItem('stadium_session_key');
  if (!key) {
    key = 'stadium-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem('stadium_session_key', key);
  }
  return key;
}

function StadiumPageInner({ forcedDay = null, hideDaySwitcher = false, posMode = false, onPosSeatHoldCreated = null }) {
  const { content } = useCms();
  const artistLineup = getArtistLineup(content);
  const ampArtistNames = {
    saturday: formatArtistNames(getLineupArtists(artistLineup, { day: 'saturday', stage: 'pac-amp', role: 'artist' })),
    sunday: formatArtistNames(getLineupArtists(artistLineup, { day: 'sunday', stage: 'pac-amp', role: 'artist' })),
  };
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const camRef = useRef({ x: 0, y: 0, scale: 1 });
  const seatsCacheRef = useRef({});
  const sizeRef = useRef({ w: 0, h: 0 });
  const animatingRef = useRef(false);
  const touchRef = useRef({ dist: 0, center: null, lastPos: null, isPinching: false, lastPinchTime: 0 });
  const dragRef = useRef({ startX: 0, startY: 0, prevX: 0, prevY: 0, isDragging: false, isDown: false });

  const { isAuthenticated, user } = useAuth();

  // Override body black background to prevent bleed-through on mobile
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#f1f5f9';
    return () => { document.body.style.backgroundColor = prev; };
  }, []);

  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingExit, setLoadingExit] = useState(false);
  const dataReadyRef = useRef(false);
  const loadingTargetRef = useRef(0);
  const loadingTimerRef = useRef(null);

  // Smooth progress: tick toward target, slow-creep between milestones
  const setLoadingTarget = useCallback((target) => {
    loadingTargetRef.current = target;
  }, []);

  useEffect(() => {
    if (!loading) { clearInterval(loadingTimerRef.current); return; }
    loadingTimerRef.current = setInterval(() => {
      setLoadingProgress(prev => {
        const target = loadingTargetRef.current;
        if (target >= 100) {
          // final sprint to 100
          const step = Math.max((100 - prev) * 0.22, 0.8);
          return Math.min(prev + step, 100);
        }
        if (prev >= target) {
          // slow creep past target — never stalls, caps at 95
          if (prev >= 95) return prev;
          const rate = prev < target + 5 ? 0.25 : 0.12;
          return Math.min(prev + rate, 95);
        }
        // ease toward target: close gap by ~18% per tick
        const step = Math.max((target - prev) * 0.18, 0.5);
        return Math.min(prev + step, target);
      });
    }, 60);
    return () => clearInterval(loadingTimerRef.current);
  }, [loading]);

  // Cinematic exit: once progress reaches 100 and data is ready, zoom-out then hide
  useEffect(() => {
    if (!loading || loadingExit) return;
    if (loadingProgress >= 99.5 && dataReadyRef.current) {
      // Let canvas/camera init immediately by setting loading=false now
      setLoading(false);
      dataReadyRef.current = false;
      // Keep overlay visible for the zoom-out animation
      setLoadingExit(true);
      setTimeout(() => { setLoadingExit(false); }, 750);
    }
  }, [loading, loadingProgress, loadingExit]);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [tooltipData, setTooltipData] = useState(null);
  const [panelOpacity, setPanelOpacity] = useState(0);
  const [mobileSeatView, setMobileSeatView] = useState(null);
  const [adminSeatInfo, setAdminSeatInfo] = useState(null); // { label, buyerInfo, x, y }
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [samAssignLoading, setSamAssignLoading] = useState(false);
  const [samAssignError, setSamAssignError] = useState('');
  const [showSamAssignModal, setShowSamAssignModal] = useState(false);
  const [samAssignSeatTargets, setSamAssignSeatTargets] = useState([]);
  const isMobile = canvasSize.w > 0 && canvasSize.w < 768;
  const isStaffOrAdmin = useMemo(() => isCurrentUserAdmin(), []);
  const isStrictAdmin = useMemo(() => isCurrentUserStrictAdmin(), []);
  const actorEmail = useMemo(() => String(user?.email || '').trim().toLowerCase(), [user?.email]);

  // ── Day switching state (support ?day=sunday URL param) ──
  const [searchParams] = useSearchParams();
  // Prefill from the assignment flow: ?assignEmail=&assignFirst=&assignLast=&assignSendEmail=
  const assignPrefill = useMemo(() => ({
    email: searchParams.get('assignEmail') || '',
    first: searchParams.get('assignFirst') || '',
    last: searchParams.get('assignLast') || '',
    // "Email ticket confirmation" toggle carried over from the HQ Assign page.
    sendEmail: searchParams.get('assignSendEmail') === '1',
  }), [searchParams]);
  const isPrefillAssignMode = useMemo(
    () => !!(assignPrefill.email || assignPrefill.first || assignPrefill.last),
    [assignPrefill.email, assignPrefill.first, assignPrefill.last]
  );
  const isAdminAssignMode = isStrictAdmin || isPrefillAssignMode;
  const initialDay = forcedDay || (searchParams.get('day') === 'sunday' ? 'sunday' : 'saturday');
  const [selectedDay, setSelectedDay] = useState(initialDay);
  const [dayStatus, setDayStatus] = useState({
    saturday: { enabled: true },
    sunday: { enabled: false }
  });
  const selectedDayRef = useRef(initialDay);
  const [hasVendorInCart, setHasVendorInCart] = useState(false);

  // ── Vendor booth mutual exclusivity (matches amphitheater-tickets) ──
  useEffect(() => {
    const checkVendorInCart = () => {
      try {
        const stored = localStorage.getItem('cart');
        if (stored) {
          const parsed = JSON.parse(stored);
          const items = parsed.items || [];
          setHasVendorInCart(items.some(item =>
            item.type === 'vendor-booth' || item.type === 'vendor' || item.type === 'vendor-attendee'
          ));
        } else {
          setHasVendorInCart(false);
        }
      } catch { setHasVendorInCart(false); }
    };
    checkVendorInCart();
    window.addEventListener('cartUpdated', checkVendorInCart);
    window.addEventListener('storage', checkVendorInCart);
    return () => {
      window.removeEventListener('cartUpdated', checkVendorInCart);
      window.removeEventListener('storage', checkVendorInCart);
    };
  }, []);

  // ── Meta Pixel: ViewContent on page load (matches amphitheater-tickets) ──
  useEffect(() => {
    trackViewContent({
      contentName: 'Amphitheater Tickets',
      contentCategory: 'Amphitheater',
      value: 0,
      currency: 'USD',
    });
  }, []);

  // ── Lock body/html scroll to prevent mobile viewport scroll issues ──
  // On mobile, .app min-height:100vh > 100dvh, creating invisible scroll space
  // that can push the header off-screen on initial load or touch interaction.
  // Delay scroll lock to ensure header renders first on production.
  useEffect(() => {
    // Force Safari to recalculate visual viewport immediately on mount
    window.scrollTo(0, 0);
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const {
    viewMode, activeSection, hoveredSection, hoveredSeat,
    selectedSeats, seatMap,
    setHoveredSection, setHoveredSeat,
    toggleSeatSelection, toggleBoxSelection, clearSelection, setSeatMap,
    zoomToSection, zoomOut,
    adminBypassSoldOut, setAdminBypassSoldOut,
  } = useStadiumStore();

  useEffect(() => {
    if (forcedDay && forcedDay !== selectedDay) {
      clearSelection();
      setTooltipData(null);
      if (viewMode === 'section') zoomOut();
      setSelectedDay(forcedDay);
    }
  }, [forcedDay, selectedDay, clearSelection, viewMode, zoomOut]);

  // ── Schedule a canvas redraw ──
  const scheduleDraw = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      performDraw(canvasRef.current, sizeRef.current.w, sizeRef.current.h, camRef.current, seatsCacheRef.current);
    });
  }, []);

  // Redraw whenever the admin toggles the sold-out bypass so the canvas
  // immediately reflects the new sold-out shading/clickability.
  useEffect(() => {
    scheduleDraw();
  }, [adminBypassSoldOut, scheduleDraw]);

  // Expose scheduleDraw for external triggers
  useEffect(() => {
    window.__stadiumScheduleDraw = scheduleDraw;
    return () => { delete window.__stadiumScheduleDraw; };
  }, [scheduleDraw]);

  // ── Load designer color scheme (v3 JSON) on mount ──
  // Previously handled by StadiumDebugger; kept here so canvas + UI overlays
  // render with the correct production color palette.
  useEffect(() => {
    fetch('/stadium-colors-v3.json')
      .then(r => r.json())
      .then(colors => {
        window.__stadiumDebugStyle = colors;
        // Apply CSS custom properties for HTML overlay elements
        const root = document.documentElement;
        Object.keys(colors).forEach(k => {
          if (k.startsWith('--dbg-')) root.style.setProperty(k, colors[k]);
        });
        scheduleDraw();
      })
      .catch(() => {});
  }, [scheduleDraw]);

  // ── Generate seats once ──
  const allSeats = useMemo(() => {
    const map = {};
    SECTIONS.forEach(sec => {
      const seats = generateRadialSeats(sec);
      seats.forEach(s => { map[s.id] = s; });
      seatsCacheRef.current[sec.id] = seats;
    });
    ADA_SECTIONS.forEach(ada => {
      const seats = generateRadialSeats(ada);
      seats.forEach(s => { map[s.id] = s; });
      seatsCacheRef.current[ada.id] = seats;
    });
    return map;
  }, []);

  // ── Reusable: apply pricing from JSON data + multiplier + overrides ──
  const applyPricing = useCallback((jsonData, mult, day) => {
    // ── Step 1: Build pricingData from JSON (identical to amp-tickets) ──
    const applyMult = (price) => mult === 1.0 ? price : Math.max(1, Math.round(price * mult));
    const modelEnabled = Boolean(pricingModelRef.current?.enabled);
    const pricingSeats = modelEnabled
      ? applyAmpPricingModelToSeats({ seats: jsonData.seats, day, model: pricingModelRef.current, overrides: priceOverridesRef.current, multiplier: mult })
      : jsonData.seats;
    const sectionData = {};
    const perSeatPrices = {};
    pricingSeats.forEach(seat => {
      const sec = seat.section;
      if (!sectionData[sec]) sectionData[sec] = { rows: {}, allPrices: [] };
      if (!sectionData[sec].rows[seat.row]) sectionData[sec].rows[seat.row] = { total: 0, count: 0 };
      const adjusted = modelEnabled ? seat.effective_price : applyMult(seat.price);
      sectionData[sec].rows[seat.row].total += adjusted;
      sectionData[sec].rows[seat.row].count++;
      sectionData[sec].allPrices.push(adjusted);
      perSeatPrices[`${sec}|${seat.row}|${seat.seatNumber}`] = adjusted;
    });
    const pricingData = {};
    Object.entries(sectionData).forEach(([secName, d]) => {
      const rowPrices = Object.entries(d.rows)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, r]) => Math.round(r.total / r.count));
      pricingData[secName] = {
        rowPrices,
        minPrice: Math.min(...d.allPrices),
        maxPrice: Math.max(...d.allPrices),
      };
    });

    // ── Step 2: Get pricing overrides ──
    const dayKey = day || 'saturday';
    const backendOverrides = priceOverridesRef.current[dayKey] || {};
    const localOverrides = getDayPricingOverrides(dayKey);
    const mergedOverrides = { ...backendOverrides, ...localOverrides };

    // ── Step 3: getRowPrice — identical to amp-tickets formula ──
    const getRowPrice = (sp, rowIdx, totalRows, fallback) => {
      if (!sp?.rowPrices?.length) return fallback;
      const idx = Math.min(
        Math.floor(rowIdx / Math.max(totalRows, 1) * sp.rowPrices.length),
        sp.rowPrices.length - 1
      );
      return sp.rowPrices[idx];
    };

    // ── Step 4: Apply pricing to all sections ──
    const boxPd = pricingData['Box Seats'];
    const isSunday = dayKey === 'sunday';
    const boxTotal = isSunday && !modelEnabled ? 1224 : (boxPd?.minPrice ?? applyMult(2300));

    const allSections = [...SECTIONS, ...ADA_SECTIONS];
    allSections.forEach(sec => {
      const seats = seatsCacheRef.current[sec.id];
      if (!seats) return;

      const secOverride = mergedOverrides[sec.dataKey] || null;
      const rowOverrides = secOverride?.rows || {};
      const sp = pricingData[sec.dataKey];

      if (sec.isBoxSection) {
        // ── Box seats: remainder pattern (identical to amp-tickets) ──
        const byBox = {};
        seats.forEach(seat => {
          if (!byBox[seat.boxId]) byBox[seat.boxId] = [];
          byBox[seat.boxId].push(seat);
        });

        Object.entries(byBox).forEach(([, boxSeats]) => {
          const n = boxSeats.length;
          const bp = Number((boxTotal / n).toFixed(2));
          let rem = boxTotal;
          boxSeats.forEach((seat, i) => {
            const isLast = i === n - 1;
            seat.price = isLast ? Number(rem.toFixed(2)) : bp;
            rem = Number((rem - seat.price).toFixed(2));
            if (rowOverrides[seat.row] != null) {
              seat.price = applyMult(parseInt(rowOverrides[seat.row]));
            }
          });
        });

        // Section display = total box price (not per-seat)
        sec.priceRange = [boxTotal, boxTotal];
        sec.displayPrice = `$${boxTotal.toLocaleString()}`;
      } else if (sec.isADA) {
        // ── ADA seats: use per-seat price from JSON, fallback to minPrice ──
        const adaFallback = sp?.minPrice ?? applyMult(sec.priceRange?.[0] || 0);
        let minP = Infinity, maxP = 0;

        seats.forEach(seat => {
          const seatKey = `${sec.dataKey}|${seat.row}|${seat.seatNumber}`;
          seat.price = perSeatPrices[seatKey] != null ? perSeatPrices[seatKey] : adaFallback;

          if (rowOverrides[seat.row] != null) {
            seat.price = applyMult(parseInt(rowOverrides[seat.row]));
          }

          if (seat.price > 0) {
            minP = Math.min(minP, seat.price);
            maxP = Math.max(maxP, seat.price);
          }
        });

        if (minP < Infinity) {
          sec.priceRange = [minP, maxP];
          sec.displayPrice = `$${minP.toLocaleString()}+`;
        }
      } else {
        // ── Regular seats: row-index interpolation (identical to amp-tickets) ──
        const totalRows = sec.rows.length;
        const fallback = sp?.minPrice ?? applyMult(sec.priceRange?.[0] || 0);
        let minP = Infinity, maxP = 0;

        seats.forEach(seat => {
          const seatKey = `${sec.dataKey}|${seat.row}|${seat.seatNumber}`;
          if (perSeatPrices[seatKey] != null) {
            seat.price = perSeatPrices[seatKey];
          } else {
            const rowIdx = sec.rows.indexOf(seat.row);
            seat.price = getRowPrice(sp, rowIdx >= 0 ? rowIdx : 0, totalRows, fallback);
          }

          if (rowOverrides[seat.row] != null) {
            seat.price = applyMult(parseInt(rowOverrides[seat.row]));
          }

          if (seat.price > 0) {
            minP = Math.min(minP, seat.price);
            maxP = Math.max(maxP, seat.price);
          }
        });

        if (minP < Infinity) {
          sec.priceRange = [minP, maxP];
          sec.displayPrice = `$${minP.toLocaleString()}+`;
        } else {
          sec.priceRange = [Math.round(sec.priceRange[0] * mult), Math.round(sec.priceRange[1] * mult)];
          sec.displayPrice = `$${sec.priceRange[0].toLocaleString()}+`;
        }
      }
    });

    const updated = {};
    allSections.forEach(sec => {
      const seats = seatsCacheRef.current[sec.id];
      if (seats) seats.forEach(s => { updated[s.id] = s; });
    });
    setSeatMap(updated);
    scheduleDraw();
  }, [setSeatMap, scheduleDraw]);

  // ── Multiplier ref (avoids re-triggering the load effect) ──
  const ampMultRef = useRef(null);
  const priceOverridesRef = useRef({});
  const pricingModelRef = useRef(null);
  // ── Admin: purchaser info for sold seats (keyed by stadium seat ID) ──
  const seatBuyerMapRef = useRef({});
  const isAdminRef = useRef(isCurrentUserAdmin());
  const samAssignableSeatMapRef = useRef({});
  const selectedAssignableSeatIdsRef = useRef(new Set());
  const [selectedAssignableSeatIds, setSelectedAssignableSeatIds] = useState([]);
  const [assignablePoolOptions, setAssignablePoolOptions] = useState([]);

  // ── Helper: build sold seat IDs + buyer map from API data ──
  const buildSoldIds = useCallback((soldData) => {
    const soldIds = new Set();
    const buyerMap = {};
    let unmapped = 0;
    const addBuyer = (seatId, sold) => {
      if (sold.buyer_email || sold.buyer_name) {
        buyerMap[seatId] = {
          email: sold.buyer_email || '',
          name: sold.buyer_name || '',
          phone: sold.buyer_phone || '',
          order: sold.order_number || '',
          isComp: !!sold.is_comp,
          paidCents: sold.paid_cents || 0,
          purchasedAt: sold.purchased_at || '',
        };
      }
    };
    soldData.filter(s => s.is_available === false).forEach(sold => {
      const apiSid = sold.section_id;
      const row = String(sold.row);
      const num = parseInt(sold.seat_number);
      if (isNaN(num)) return;
      if (apiSid && apiSid.startsWith('box-')) {
        const id = `boxes-B${apiSid.replace('box-', '')}-${row}-${num}`;
        soldIds.add(id);
        addBuyer(id, sold);
        return;
      }
      if (ADA_TO_STADIUM[apiSid]) {
        let targetSid = ADA_TO_STADIUM[apiSid];
        // orch-3-ada covers both front (row B) and back (row Z) ADA bands
        if (apiSid === 'orch-3-ada' && row === 'B') {
          targetSid = 'section3-ada-front';
        }
        const id = `${targetSid}-${row}-${num}`;
        soldIds.add(id);
        addBuyer(id, sold);
        return;
      }
      const stadiumSid = API_TO_STADIUM[apiSid];
      if (!stadiumSid) { unmapped++; return; }
      const adaMap = ADA_ROWS[stadiumSid];
      // ADA sections cover limited seat ranges; seats outside belong to main section.
      // Section 1 & 3 Row Z: ADA = seats 1-28, regular = seats 44-54.
      // Section 3 Row B: ADA front = seats 1-14.
      // Sections 5 & 7 Row Z: entirely ADA (no limit).
      const ADA_MAX_SEAT = { 'section1-ada': 28, 'section3-ada-back': 28, 'section3-ada-front': 14 };
      let targetSid = stadiumSid;
      if (adaMap && adaMap[row]) {
        const adaSid = adaMap[row];
        const maxSeat = ADA_MAX_SEAT[adaSid];
        targetSid = (!maxSeat || num <= maxSeat) ? adaSid : stadiumSid;
      }
      const id = `${targetSid}-${row}-${num}`;
      soldIds.add(id);
      addBuyer(id, sold);
    });
    return { soldIds, unmapped, buyerMap };
  }, []);

  const applySamAssignableSeats = useCallback((blockedRes) => {
    const grouped = blockedRes?.data?.seats_by_date_section || {};
    const poolOptions = Array.isArray(blockedRes?.data?.pool_options) ? blockedRes.data.pool_options : [];
    setAssignablePoolOptions(poolOptions);
    const currentMap = useStadiumStore.getState().seatMap;
    const nextMap = { ...currentMap };
    const assignableById = {};
    const previouslySelected = new Set(selectedAssignableSeatIdsRef.current || []);
    const nextSelected = [];

    Object.keys(nextMap).forEach((seatId) => {
      const seatState = nextMap[seatId];
      if (!seatState) return;
      if (isAssignableStatus(seatState.status)) {
        nextMap[seatId] = { ...seatState, status: 'available' };
      }
    });

    Object.values(grouped).forEach((bySection) => {
      Object.values(bySection || {}).forEach((seatRows) => {
        (seatRows || []).forEach((blockedSeat) => {
          if (!blockedSeat || blockedSeat.is_assigned) return;
          const seatId = mapBlockedSeatToStadiumSeatId(blockedSeat);
          if (!seatId || !nextMap[seatId]) return;
          const normalizedRow = normalizeBlockedSeatRow(blockedSeat.section_display || blockedSeat.section || '', blockedSeat.row);
          const poolKey = String(blockedSeat.pool_key || 'shared').trim().toLowerCase() || 'shared';
          const keepSelected = previouslySelected.has(seatId);
          nextMap[seatId] = { ...nextMap[seatId], status: makeAssignableStatus(poolKey, keepSelected) };
          if (keepSelected) nextSelected.push(seatId);
          assignableById[seatId] = {
            ...blockedSeat,
            seat_id: seatId,
            seat_section: blockedSeat.section,
            section_label: blockedSeat.section_display || blockedSeat.section || '',
            seat_row: normalizedRow,
            seat_number: blockedSeat.seat_number,
            pool_key: poolKey,
          };
        });
      });
    });

    samAssignableSeatMapRef.current = assignableById;
    selectedAssignableSeatIdsRef.current = new Set(nextSelected);
    setSelectedAssignableSeatIds(nextSelected);
    setSeatMap(nextMap);
    scheduleDraw();
    return Object.keys(assignableById).length;
  }, [setSeatMap, scheduleDraw]);

  const loadSamAssignableSeats = useCallback(async (eventDate) => {
    if (!isStaffOrAdmin || !api.getToken()) {
      samAssignableSeatMapRef.current = {};
      selectedAssignableSeatIdsRef.current = new Set();
      setSelectedAssignableSeatIds([]);
      setAssignablePoolOptions([]);
      return 0;
    }
    try {
      const res = await api.getAdminBlockedSeats(BLOCKED_POOL_OWNER_EMAIL, eventDate);
      if (res?.success) {
        return applySamAssignableSeats(res);
      }
    } catch (err) {
      console.warn('[Stadium] Could not load Sam blocked seats:', err?.message || err);
    }
    samAssignableSeatMapRef.current = {};
    selectedAssignableSeatIdsRef.current = new Set();
    setSelectedAssignableSeatIds([]);
    setAssignablePoolOptions([]);
    return 0;
  }, [applySamAssignableSeats, isStaffOrAdmin]);

  const clearAssignableSeatSelection = useCallback(() => {
    const currentMap = useStadiumStore.getState().seatMap;
    const nextMap = { ...currentMap };
    let changed = false;
    selectedAssignableSeatIdsRef.current.forEach((seatId) => {
      const seatState = nextMap[seatId];
      const assignableState = parseAssignableStatus(seatState?.status);
      if (!seatState || !assignableState) return;
      nextMap[seatId] = { ...seatState, status: makeAssignableStatus(assignableState.poolKey, false) };
      changed = true;
    });
    selectedAssignableSeatIdsRef.current = new Set();
    setSelectedAssignableSeatIds([]);
    if (changed) {
      setSeatMap(nextMap);
      scheduleDraw();
    }
  }, [setSeatMap, scheduleDraw]);

  const toggleAssignableSeatSelection = useCallback((seatId) => {
    if (!seatId) return;
    const blockedSeat = samAssignableSeatMapRef.current[seatId];
    if (!blockedSeat) return;

    const currentMap = useStadiumStore.getState().seatMap;
    const seatState = currentMap[seatId];
    const assignableState = parseAssignableStatus(seatState?.status);
    if (!seatState || !assignableState) return;

    const nextSelectedSet = new Set(selectedAssignableSeatIdsRef.current || []);
    const isSelected = !!assignableState.selected;
    if (isSelected) nextSelectedSet.delete(seatId);
    else nextSelectedSet.add(seatId);

    const nextMap = {
      ...currentMap,
      [seatId]: {
        ...seatState,
        status: makeAssignableStatus(assignableState.poolKey, !isSelected),
      },
    };

    selectedAssignableSeatIdsRef.current = nextSelectedSet;
    setSelectedAssignableSeatIds(Array.from(nextSelectedSet));
    setSeatMap(nextMap);
    scheduleDraw();
  }, [setSeatMap, scheduleDraw]);

  const selectedAssignableTargets = useMemo(() => (
    selectedAssignableSeatIds
      .map((seatId) => samAssignableSeatMapRef.current[seatId])
      .filter(Boolean)
  ), [selectedAssignableSeatIds]);

  // ── Helper: apply sold seat data from API response to seatMap ──
  const applySoldData = useCallback((availRes) => {
    if (availRes.is_admin) {
      isAdminRef.current = true;
      console.log('[Stadium] Admin detected by backend — buyer info available');
    }
    const { soldIds, unmapped, buyerMap } = buildSoldIds(availRes.data);
    seatBuyerMapRef.current = buyerMap;
    const currentMap = useStadiumStore.getState().seatMap;
    const withSold = { ...currentMap };
    let matched = 0;
    soldIds.forEach(id => {
      if (withSold[id]) {
        withSold[id] = { ...withSold[id], status: 'sold' };
        matched++;
      }
    });
    setSeatMap(withSold);
    scheduleDraw();
    return { soldIds, unmapped, buyerMap, matched };
  }, [buildSoldIds, setSeatMap, scheduleDraw]);

  // ── Initial data load + reload when day changes ──
  // Optimization: Sold seats API fires in parallel with config+pricing (not after).
  // Total wait = max(config+pricing, soldSeats) instead of sequential.
  // Backend has 5-min cache so sold seats usually resolves in <500ms (cache hit).
  // Loading overlay stays until ALL data (including sold seats) is ready.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadingProgress(0);
    setLoadingExit(false);
    loadingTargetRef.current = 0;
    dataReadyRef.current = false;
    selectedDayRef.current = selectedDay;

    // 1. Reset ALL seat statuses to 'available' (clears previous day's sold markers)
    samAssignableSeatMapRef.current = {};
    selectedAssignableSeatIdsRef.current = new Set();
    setSelectedAssignableSeatIds([]);
    setAssignablePoolOptions([]);
    const freshMap = {};
    [...SECTIONS, ...ADA_SECTIONS].forEach(sec => {
      const seats = seatsCacheRef.current[sec.id];
      if (seats) seats.forEach(s => {
        s.status = 'available';
        freshMap[s.id] = s;
      });
    });
    setSeatMap(freshMap);

    const eventDate = DAY_TO_EVENT_DATE[selectedDay] || DAY_TO_EVENT_DATE.saturday;
    const jsonUrl = DAY_TO_JSON[selectedDay] || DAY_TO_JSON.saturday;

    (async () => {
      try {
        // 2. Fire sold-seats request IMMEDIATELY (runs in parallel with config+pricing)
        //    .catch() prevents unhandled rejection if it rejects before being awaited
        const hasToken = !!api.getToken();
        if (!cancelled) setLoadingTarget(15);
        const soldPromise = Promise.race([
          api.getAmphitheaterSeats(eventDate, { withAuth: hasToken }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000)),
        ]).catch(err => ({ __soldError: err }));

        // 3. Fetch config (with retry, 5s timeout per attempt) + pricing JSON in parallel
        const fetchConfig = async () => {
          if (ampMultRef.current != null) return null;
          for (let i = 0; i < 3; i++) {
            try {
              const res = await Promise.race([
                api.getPublicConfig(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('config timeout')), 5000)),
              ]);
              if (res?.success) return res;
            } catch { /* retry */ }
            if (i < 2) await new Promise(r => setTimeout(r, 800 * (i + 1)));
          }
          return null;
        };
        const [configRes, jsonData] = await Promise.all([
          fetchConfig(),
          fetch(jsonUrl).then(r => r.json()),
        ]);
        if (cancelled) return;
        if (!cancelled) setLoadingTarget(40);

        // 4. Process config result (day status + multiplier + overrides)
        if (configRes?.success && configRes.data) {
          const satEnabled = configRes.data.amphitheater_saturday_enabled ?? true;
          const sunEnabled = (configRes.data.amphitheater_sunday_enabled ?? false) || isCurrentUserAdmin();
          setDayStatus({
            saturday: { enabled: satEnabled },
            sunday: { enabled: sunEnabled }
          });
          const mult = configRes.data.amp_price_multiplier || 0.40;
          ampMultRef.current = mult;
          if (configRes.data.amp_price_overrides) {
            priceOverridesRef.current = configRes.data.amp_price_overrides;
          }
          if (configRes.data.amp_pricing_model) {
            pricingModelRef.current = configRes.data.amp_pricing_model;
          }
        }

        const mult = ampMultRef.current != null ? ampMultRef.current : 0.40;
        if (ampMultRef.current == null) ampMultRef.current = mult;

        // 5. Apply pricing to seat objects
        applyPricing(jsonData, mult, selectedDay);
        if (!cancelled) setLoadingTarget(60);

        // 6. Await sold seats (already in-flight since step 2, often already resolved)
        if (!cancelled) setLoadingTarget(75);
        let soldLoaded = false;
        const firstRes = await soldPromise; // never rejects (.catch above)
        if (cancelled) return;
        if (firstRes?.__soldError) {
          console.warn('[Stadium] Sold seats attempt 1 failed:', firstRes.__soldError?.message || firstRes.__soldError);
        } else if (firstRes?.success && firstRes?.data) {
          const { soldIds, unmapped, buyerMap, matched } = applySoldData(firstRes);
          console.log(`[Stadium] ${eventDate}: ${firstRes.data.length} sold, mapped ${soldIds.size}, matched ${matched}${unmapped ? `, unmapped ${unmapped}` : ''}${Object.keys(buyerMap).length ? `, admin buyers: ${Object.keys(buyerMap).length}` : ''}`);
          soldLoaded = true;
        }

        // 7. Retry up to 2 more times if first attempt failed
        if (!soldLoaded) {
          for (let attempt = 1; attempt < 3; attempt++) {
            if (cancelled) return;
            await new Promise(r => setTimeout(r, 1000 * attempt));
            const retryRes = await Promise.race([
              api.getAmphitheaterSeats(eventDate, { withAuth: hasToken }),
              new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000)),
            ]).catch(err => ({ __soldError: err }));
            if (cancelled) return;
            if (retryRes?.__soldError) {
              console.warn(`[Stadium] Attempt ${attempt + 1} failed:`, retryRes.__soldError?.message);
              continue;
            }
            if (retryRes?.success && retryRes?.data) {
              const { soldIds, matched } = applySoldData(retryRes);
              console.log(`[Stadium] ${eventDate}: retry ${attempt} — ${retryRes.data.length} sold, mapped ${soldIds.size}, matched ${matched}`);
              soldLoaded = true;
              break;
            }
          }
        }
        if (!soldLoaded) console.warn(`[Stadium] Failed to load sold seats for ${eventDate} after 3 attempts`);
        if (!cancelled) setLoadingTarget(88);
        const samAssignableCount = await loadSamAssignableSeats(eventDate);
        if (samAssignableCount) {
          console.log(`[Stadium] ${eventDate}: surfaced ${samAssignableCount} Sam seat(s) for admin/staff assignment`);
        }
        if (!cancelled) setLoadingTarget(100);
      } catch (err) {
        console.warn('[Stadium] Could not load data:', err);
      } finally {
        if (!cancelled) {
          dataReadyRef.current = true;
        }
      }
    })();

    return () => { cancelled = true; };
  }, [selectedDay, allSeats, setSeatMap, applyPricing, applySoldData, scheduleDraw, setLoadingTarget, loadSamAssignableSeats]);

  // ── Cart badge count sync ──
  useEffect(() => {
    const syncCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('cart') || '{}');
        const items = cart.items || [];
        setCartItemCount(items.reduce((n, i) => n + (i.quantity || 0), 0));
      } catch { setCartItemCount(0); }
    };
    syncCount();
    const onCartUpdate = (e) => {
      const items = e.detail?.items || [];
      setCartItemCount(items.reduce((n, i) => n + (i.quantity || 0), 0));
    };
    const onOpenCart = () => { setIsCartOpen(true); document.body.style.overflow = 'hidden'; };
    window.addEventListener('cartUpdated', onCartUpdate);
    window.addEventListener('openCart', onOpenCart);
    return () => {
      window.removeEventListener('cartUpdated', onCartUpdate);
      window.removeEventListener('openCart', onOpenCart);
    };
  }, []);

  // ── Resize (only place that sets canvas pixel dimensions) ──
  // Re-runs when loading finishes so canvas gets measured after mount
  useEffect(() => {
    const onResize = () => {
      if (!containerRef.current || !canvasRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      if (!r.width) return;
      const dpr = window.devicePixelRatio || 1;
      const c = canvasRef.current;
      c.width = r.width * dpr;
      c.height = r.height * dpr;
      c.style.width = r.width + 'px';
      c.style.height = r.height + 'px';
      sizeRef.current = { w: r.width, h: r.height };
      setCanvasSize({ w: r.width, h: r.height });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [loading]);

  // ── Compute fit-all camera ──
  const getFit = useCallback(() => {
    const { w, h } = sizeRef.current;
    if (!w) return { x: 0, y: 0, scale: 1 };
    const mobile = w < 768;
    // On mobile, reserve space for header (~60px) and bottom controls (~80px)
    const headerH = mobile ? 60 : 0;
    const bottomH = mobile ? 80 : 0;
    const usableH = h - headerH - bottomH;
    const fillFactor = mobile ? 1.15 : 0.9;
    const s = Math.min(w / CANVAS_W, usableH / CANVAS_H) * fillFactor;
    const x = (w - CANVAS_W * s) / 2;
    // On mobile, center within the usable area (below header)
    const y = mobile
      ? headerH + (usableH - CANVAS_H * s) / 2
      : (h - CANVAS_H * s) / 2;
    return { x, y, scale: s };
  }, []);

  // ── Clamp camera to keep stadium visible ──
  const clampCamera = useCallback((cam) => {
    const { w, h } = sizeRef.current;
    if (!w) return cam;
    // Generous padding so user can drag freely in every direction
    // Only prevents dragging the stadium completely off-screen
    const worldW = CANVAS_W * cam.scale;
    const worldH = CANVAS_H * cam.scale;
    const padX = w * 0.5;
    const padY = h * 0.5;
    return {
      x: Math.min(padX, Math.max(w - worldW - padX, cam.x)),
      y: Math.min(padY, Math.max(h - worldH - padY, cam.y)),
      scale: cam.scale,
    };
  }, []);

  // ── Initial camera ──
  useEffect(() => {
    if (loading || !canvasSize.w) return;
    const fit = getFit();
    camRef.current = { ...fit };
    scheduleDraw();
  }, [loading, canvasSize, getFit, scheduleDraw]);

  // ── Camera animation on view mode change ──
  // Uses GPU-accelerated CSS transforms — zero canvas redraws during motion
  useEffect(() => {
    if (loading || !canvasSize.w) return;
    const { w, h } = sizeRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    let targetCam;
    if (viewMode === 'overview') {
      targetCam = getFit();
      // Fade panel out
      const opDur = 300, opT0 = performance.now(), opStart = panelOpacity;
      const fadeOp = (now) => { const p = Math.min((now - opT0) / opDur, 1); setPanelOpacity(opStart * (1 - p)); if (p < 1) requestAnimationFrame(fadeOp); };
      requestAnimationFrame(fadeOp);
    } else if (activeSection) {
      const mobile = w < 768;
      const t = getZoomTarget(activeSection, w, h, mobile ? 1.05 : undefined);
      targetCam = {
        x: w / 2 - t.cx * t.scale,
        y: h / 2 - t.cy * t.scale,
        scale: t.scale,
      };
      // Fade panel in (delayed)
      setTimeout(() => {
        const opDur2 = 500, opT02 = performance.now(), opStart2 = panelOpacity;
        const fadeOp2 = (now) => { const p = Math.min((now - opT02) / opDur2, 1); setPanelOpacity(opStart2 + (1 - opStart2) * p); if (p < 1) requestAnimationFrame(fadeOp2); };
        requestAnimationFrame(fadeOp2);
      }, 250);
    }
    if (!targetCam) return;

    const cam0 = camRef.current;
    const s0 = cam0.scale || 0.001;

    // CSS transform mapping: current rendered view → target view
    const ss = targetCam.scale / s0;
    const tx = targetCam.x - cam0.x * ss;
    const ty = targetCam.y - cam0.y * ss;

    animatingRef.current = true;
    canvas.style.willChange = 'transform';
    canvas.style.transformOrigin = '0px 0px';

    const duration = 550;
    const t0 = performance.now();
    let animFrame;
    const ease = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; // power2.inOut
    const animate = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const ep = ease(p);
      const cx = tx * ep;
      const cy = ty * ep;
      const cs = 1 + (ss - 1) * ep;
      canvas.style.transform = `translate(${cx}px, ${cy}px) scale(${cs})`;
      if (p < 1) {
        animFrame = requestAnimationFrame(animate);
      } else {
        camRef.current = { ...targetCam };
        performDraw(canvas, w, h, camRef.current, seatsCacheRef.current);
        canvas.style.transform = '';
        canvas.style.willChange = '';
        canvas.style.transformOrigin = '';
        animatingRef.current = false;
      }
    };
    animFrame = requestAnimationFrame(animate);

    return () => {
      if (animFrame) cancelAnimationFrame(animFrame);
      // Clear stale CSS styles if animation was interrupted
      if (canvas) {
        canvas.style.transform = '';
        canvas.style.willChange = '';
        canvas.style.transformOrigin = '';
      }
      animatingRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeSection, canvasSize]);

  // ── Scroll wheel zoom (works in both overview and section view) ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e) => {
      e.preventDefault();
      if (animatingRef.current) return;
      const st = useStadiumStore.getState();

      const cam = camRef.current;
      const fit = getFit();

      const minScale = fit.scale * 0.95;
      const maxScale = fit.scale * 20;

      const zoomFactor = 1 - e.deltaY * 0.002;
      const newScale = Math.min(maxScale, Math.max(minScale, cam.scale * zoomFactor));
      if (newScale === cam.scale) return;

      // If scrolling out near fit scale while in section view, snap to overview
      if (st.viewMode === 'section' && newScale <= fit.scale * 1.05) {
        setTooltipData(null);
        setAdminSeatInfo(null);
        zoomOut();
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      // Keep world point under cursor fixed
      const wx = (mx - cam.x) / cam.scale;
      const wy = (my - cam.y) / cam.scale;

      camRef.current = clampCamera({
        x: mx - wx * newScale,
        y: my - wy * newScale,
        scale: newScale,
      });

      scheduleDraw();
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [getFit, clampCamera, scheduleDraw, zoomOut, setTooltipData]);

  // ── Touch pinch zoom + pan (direct synchronous draws, throttled 60fps) ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const tr = touchRef.current;
    let lastDraw = 0;

    const dist = (t) => {
      const dx = t[0].clientX - t[1].clientX;
      const dy = t[0].clientY - t[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // Synchronous draw, throttled to ~60fps to stay smooth
    const drawThrottled = () => {
      const now = performance.now();
      if (now - lastDraw < 16) return;
      lastDraw = now;
      const { w, h } = sizeRef.current;
      performDraw(canvas, w, h, camRef.current, seatsCacheRef.current);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        tr.isPinching = true;
        tr.didMove = true;
        tr.dist = dist(e.touches);
      } else if (e.touches.length === 1) {
        tr.lastPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        tr.didMove = false;
      }
    };

    const onTouchMove = (e) => {
      if (animatingRef.current) return;
      const st = useStadiumStore.getState();
      if (e.touches.length === 2 && tr.isPinching) {
        e.preventDefault();

        const newDist = dist(e.touches);
        if (tr.dist > 0) {
          const scaleFactor = newDist / tr.dist;
          const cam = camRef.current;
          const fit = getFit();

          const minScale = fit.scale * 0.95;
          const maxScale = fit.scale * 20;

          const newScale = Math.min(maxScale, Math.max(minScale, cam.scale * scaleFactor));

          // If pinch-zooming out near fit scale while in section view, snap to overview
          if (st.viewMode === 'section' && newScale <= fit.scale * 1.05) {
            setTooltipData(null);
            setAdminSeatInfo(null);
            zoomOut();
            tr.dist = newDist;
            return;
          }

          const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
          const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
          const rect = canvas.getBoundingClientRect();
          const mx = cx - rect.left, my = cy - rect.top;
          const wx = (mx - cam.x) / cam.scale;
          const wy = (my - cam.y) / cam.scale;

          camRef.current = clampCamera({ x: mx - wx * newScale, y: my - wy * newScale, scale: newScale });
          drawThrottled();
        }
        tr.dist = newDist;
      } else if (e.touches.length === 1 && !tr.isPinching && tr.lastPos) {
        e.preventDefault();
        const cam = camRef.current;
        const dx = e.touches[0].clientX - tr.lastPos.x;
        const dy = e.touches[0].clientY - tr.lastPos.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) tr.didMove = true;
        camRef.current = clampCamera({ x: cam.x + dx, y: cam.y + dy, scale: cam.scale });
        tr.lastPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        drawThrottled();
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2 && tr.isPinching) {
        tr.isPinching = false; tr.dist = 0;
        tr.lastPinchTime = Date.now();
        tr.lastPos = null;
        // Final full-quality draw
        const { w, h } = sizeRef.current;
        performDraw(canvas, w, h, camRef.current, seatsCacheRef.current);
      }
      if (e.touches.length === 0) tr.lastPos = null;
    };

    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd);
    return () => {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
    };
  }, [getFit, clampCamera, zoomOut, setTooltipData]);

  // ── Zoom button handler (+/- buttons, works in both overview and section view) ──
  const handleZoomButton = useCallback((direction) => {
    if (animatingRef.current) return;
    const st = useStadiumStore.getState();

    const cam = camRef.current;
    const { w, h } = sizeRef.current;
    const fit = getFit();

    const minScale = fit.scale * 0.95;
    const maxScale = fit.scale * 20;

    const factor = direction === 'in' ? 1.4 : 0.714;
    const newScale = Math.min(maxScale, Math.max(minScale, cam.scale * factor));
    if (newScale === cam.scale) return;

    // If zooming out near the fit scale while in section view, snap back to overview
    if (st.viewMode === 'section' && newScale <= fit.scale * 1.05) {
      setTooltipData(null);
      setAdminSeatInfo(null);
      zoomOut();
      return;
    }

    const mx = w / 2, my = h / 2;
    const wx = (mx - cam.x) / cam.scale;
    const wy = (my - cam.y) / cam.scale;

    camRef.current = clampCamera({ x: mx - wx * newScale, y: my - wy * newScale, scale: newScale });
    scheduleDraw();
  }, [getFit, clampCamera, scheduleDraw, zoomOut, setTooltipData]);

  // ── Redraw on state changes ──
  useEffect(() => { scheduleDraw(); }, [hoveredSection, hoveredSeat, selectedSeats, seatMap, scheduleDraw]);

  // ── Screen → canvas coordinates ──
  const toCanvas = useCallback((cx, cy) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const c = camRef.current;
    return { x: (cx - rect.left - c.x) / c.scale, y: (cy - rect.top - c.y) / c.scale };
  }, []);

  const isSectionSoldOut = useCallback((sectionId) => {
    // Admin bypass: treat nothing as sold out so fully-sold sections stay
    // clickable and the admin can drill into seats/prices/buyers.
    if (useStadiumStore.getState().adminBypassSoldOut) return false;

    const parentSeats = seatsCacheRef.current[sectionId] || [];
    const childAdaSeats = ADA_SECTIONS
      .filter((ada) => ada.parentId === sectionId)
      .flatMap((ada) => seatsCacheRef.current[ada.id] || []);
    const allSeats = [...parentSeats, ...childAdaSeats];

    if (!allSeats.length) return false;

    return allSeats.every((seat) => {
      const seatState = seatMap[seat.id] || seat;
      return seatState.status === 'sold' || seatState.status === 'held';
    });
  }, [seatMap]);

  const findSection = useCallback((cx, cy) => {
    // Check in reverse so top-drawn (smaller) sections get priority
    for (let i = 0; i < SECTIONS.length; i++) {
      if (hitTestArc(cx, cy, SECTIONS[i].geo)) {
        if (!isSectionSoldOut(SECTIONS[i].id)) return SECTIONS[i];
        return null;
      }
    }
    // Check ADA bands — map click to parent section
    for (let i = 0; i < ADA_SECTIONS.length; i++) {
      if (hitTestArc(cx, cy, ADA_SECTIONS[i].geo)) {
        const parent = SECTIONS.find(s => s.id === ADA_SECTIONS[i].parentId) || null;
        return parent && !isSectionSoldOut(parent.id) ? parent : null;
      }
    }
    return null;
  }, [isSectionSoldOut]);

  // eslint-disable-next-line no-unused-vars
  const findSeat = useCallback((cx, cy, secId) => {
    const seats = seatsCacheRef.current[secId];
    if (seats) {
      for (let i = seats.length - 1; i >= 0; i--) {
        const s = seats[i];
        const hr = s.radius * 1.6; // generous hit area
        const dx = cx - s.x, dy = cy - s.y;
        if (dx * dx + dy * dy < hr * hr) return s;
      }
    }
    // For boxes: hit-test sub-box arcs so clicking anywhere in a box works
    if (secId === 'boxes' && seats) {
      for (const box of BOX_DEFS) {
        if (hitTestArc(cx, cy, box.geo)) {
          // Return first seat in this box as representative
          const rep = seats.find(s => s.boxId === box.id);
          if (rep) return rep;
        }
      }
    }
    // Also check ADA seats for this section
    const adaSecs = ADA_SECTIONS.filter(a => a.parentId === secId);
    for (const adaSec of adaSecs) {
      const adaSeats = seatsCacheRef.current[adaSec.id];
      if (adaSeats) {
        for (let i = adaSeats.length - 1; i >= 0; i--) {
          const s = adaSeats[i];
          const hr = s.radius * 1.6;
          const dx = cx - s.x, dy = cy - s.y;
          if (dx * dx + dy * dy < hr * hr) return s;
        }
      }
    }
    return null;
  }, []);

  // Find a seat in the active section only (not across all sections)
  const findSeatAnywhere = useCallback((cx, cy) => {
    const st = useStadiumStore.getState();
    const activeSid = st.activeSection;
    if (!activeSid) return null;

    // Check the active section's seats
    const seats = seatsCacheRef.current[activeSid];
    if (seats) {
      for (let i = seats.length - 1; i >= 0; i--) {
        const s = seats[i];
        const hr = s.radius * 1.6;
        const dx = cx - s.x, dy = cy - s.y;
        if (dx * dx + dy * dy < hr * hr) return s;
      }
    }
    // Box sub-arc hit test (only if boxes is the active section)
    if (activeSid === 'boxes') {
      const boxSeats = seatsCacheRef.current['boxes'];
      if (boxSeats) {
        for (const box of BOX_DEFS) {
          if (hitTestArc(cx, cy, box.geo)) {
            const rep = boxSeats.find(s => s.boxId === box.id);
            if (rep) return rep;
          }
        }
      }
    }
    // Check ADA sections that belong to the active section
    const adaSecs = ADA_SECTIONS.filter(a => a.parentId === activeSid);
    for (const adaSec of adaSecs) {
      const adaSeats = seatsCacheRef.current[adaSec.id];
      if (adaSeats) {
        for (let i = adaSeats.length - 1; i >= 0; i--) {
          const s = adaSeats[i];
          const hr = s.radius * 1.6;
          const dx = cx - s.x, dy = cy - s.y;
          if (dx * dx + dy * dy < hr * hr) return s;
        }
      }
    }
    return null;
  }, []);

  // ── Pointer events (blocked during animation) ──
  const onPointerMove = useCallback((e) => {
    if (animatingRef.current) return;
    // Touch input is handled by dedicated touch handlers — skip to prevent double camera updates
    if (e.pointerType === 'touch') return;
    const dr = dragRef.current;

    // Handle mouse drag (works in both overview and section view)
    const st0 = useStadiumStore.getState(); // eslint-disable-line no-unused-vars
    if (dr.isDown && (e.buttons & 1)) {
      const dx = e.clientX - dr.startX;
      const dy = e.clientY - dr.startY;
      if (!dr.isDragging && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        dr.isDragging = true;
        if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
      }
      if (dr.isDragging) {
        const cam = camRef.current;
        const mdx = e.clientX - dr.prevX;
        const mdy = e.clientY - dr.prevY;
        camRef.current = clampCamera({ x: cam.x + mdx, y: cam.y + mdy, scale: cam.scale });
        scheduleDraw();
        dr.prevX = e.clientX;
        dr.prevY = e.clientY;
        setTooltipData(null);
        return;
      }
      dr.prevX = e.clientX;
      dr.prevY = e.clientY;
    }

    // Hover logic
    const p = toCanvas(e.clientX, e.clientY);
    const st = useStadiumStore.getState();

    if (st.viewMode === 'overview') {
      const sec = findSection(p.x, p.y);
      if (sec?.id !== st.hoveredSection) setHoveredSection(sec?.id || null);
      setTooltipData(sec ? {
        x: e.clientX, y: e.clientY,
        label: sec.label, price: sec.displayPrice, seats: sec.seatCount,
      } : null);
    } else if (st.viewMode === 'section') {
      const seat = findSeatAnywhere(p.x, p.y);
      const seatState = seat ? st.seatMap[seat.id] : null;
      const isSold = seatState && (seatState.status === 'sold' || seatState.status === 'held');
      const assignableState = parseAssignableStatus(seatState?.status);
      const isAssignable = !!assignableState;
      const adminView = isAdminRef.current;
      const assignableSeatMeta = seat ? samAssignableSeatMapRef.current[seat.id] : null;
      const assignablePoolStyle = getAssignablePoolStyle(assignableSeatMeta?.pool_key || assignableState?.poolKey);
      // Allow hover on sold seats for admins (so they get visual highlight)
      const hoverableSeat = (seat && (!isSold || adminView)) ? seat : null;
      if (hoverableSeat?.id !== st.hoveredSeat) setHoveredSeat(hoverableSeat?.id || null);
      if (seat) {
        const ss = st.seatMap[seat.id];
        const hSeat = seat;
        const viewImg = getSeatViewImage(hSeat.sectionId);
        const buyerInfo = (adminView && isSold) ? seatBuyerMapRef.current[seat.id] : null;
        if (hSeat.boxId) {
          const box = BOX_DEFS.find(b => b.id === hSeat.boxId);
          const numSeats = box?.seatCount || 8;
          const allBoxSeats = (seatsCacheRef.current['boxes'] || []).filter(s => s.boxId === hSeat.boxId);
          const boxSum = allBoxSeats.reduce((sum, s) => sum + (st.seatMap[s.id]?.price || s.price), 0);
          const boxDisplay = Math.round(boxSum);
          setTooltipData({
            x: e.clientX, y: e.clientY,
            label: `Premium ${box?.label || hSeat.boxId} · ${numSeats} seats`,
            price: isSold ? 'SOLD' : (isAssignable ? (assignableState?.selected ? 'SELECTED' : 'ASSIGN') : `$${boxDisplay.toLocaleString()}`), status: ss?.status,
            isSold,
            isAssignable,
            assignablePoolLabel: assignableSeatMeta?.pool_label || assignablePoolStyle.label,
            assignablePoolAccountEmail: assignableSeatMeta?.pool_account_email || '',
            assignablePoolColor: assignablePoolStyle.color,
            isAssignableSelected: !!assignableState?.selected,
            viewImage: viewImg,
            buyerInfo,
          });
        } else {
          setTooltipData({
            x: e.clientX, y: e.clientY,
            label: `${[...SECTIONS, ...ADA_SECTIONS].find(s => s.id === hSeat.sectionId)?.shortLabel || ''} · Row ${hSeat.row} · Seat ${hSeat.seatNumber}`,
            price: isSold ? 'SOLD' : (isAssignable ? (assignableState?.selected ? 'SELECTED' : 'ASSIGN') : `$${(ss?.price || hSeat.price).toLocaleString()}`), status: ss?.status,
            isSold,
            isAssignable,
            assignablePoolLabel: assignableSeatMeta?.pool_label || assignablePoolStyle.label,
            assignablePoolAccountEmail: assignableSeatMeta?.pool_account_email || '',
            assignablePoolColor: assignablePoolStyle.color,
            isAssignableSelected: !!assignableState?.selected,
            viewImage: viewImg,
            buyerInfo,
          });
        }
      } else {
        setTooltipData(null);
      }
    }
  }, [toCanvas, findSection, findSeatAnywhere, setHoveredSection, setHoveredSeat, clampCamera, scheduleDraw]);

  const onPointerDown = useCallback((e) => {
    if (animatingRef.current) return;
    const dr = dragRef.current;
    dr.startX = e.clientX;
    dr.startY = e.clientY;
    dr.prevX = e.clientX;
    dr.prevY = e.clientY;
    dr.isDragging = false;
    dr.isDown = true;
  }, []);

  const onPointerUp = useCallback((e) => {
    const dr = dragRef.current;
    const wasDown = dr.isDown;
    const wasDragging = dr.isDragging;
    dr.isDown = false;
    dr.isDragging = false;
    if (canvasRef.current) canvasRef.current.style.cursor = '';

    if (!wasDown || wasDragging || animatingRef.current) return;

    // Suppress taps that follow a pinch or any touch drag (prevents accidental selection)
    if (Date.now() - touchRef.current.lastPinchTime < 400) return;
    if (touchRef.current.didMove) return;

    // This was a click, not a drag
    const p = toCanvas(e.clientX, e.clientY);
    const st = useStadiumStore.getState();
    if (st.viewMode === 'overview') {
      const sec = findSection(p.x, p.y);
      if (sec) { setTooltipData(null); zoomToSection(sec.id); }
    } else if (st.viewMode === 'section') {
      const seat = findSeatAnywhere(p.x, p.y);
      const ss = seat ? st.seatMap[seat.id] : null;
      const isSold = ss && (ss.status === 'sold' || ss.status === 'held');
      const isAssignable = !!parseAssignableStatus(ss?.status);
      const adminView = isAdminRef.current;

      // Admin click on sold seat → show persistent buyer info panel
      if (seat && isSold && adminView) {
        const buyerInfo = seatBuyerMapRef.current[seat.id];
        const secObj = [...SECTIONS, ...ADA_SECTIONS].find(s => s.id === seat.sectionId);
        const box = seat.boxId ? BOX_DEFS.find(b => b.id === seat.boxId) : null;
        const label = box
          ? `Premium ${box.label || seat.boxId}`
          : `${secObj?.shortLabel || ''} · Row ${seat.row} · Seat ${seat.seatNumber}`;
        setAdminSeatInfo({ label, buyerInfo: buyerInfo || null, x: e.clientX, y: e.clientY });
        setTooltipData(null);
        return;
      }

      if (seat && isAssignable && isStaffOrAdmin) {
        const blockedSeat = samAssignableSeatMapRef.current[seat.id];
        if (blockedSeat) {
          setAdminSeatInfo(null);
          setTooltipData(null);
          setSamAssignError('');
          toggleAssignableSeatSelection(seat.id);
        }
        return;
      }

      // Block click on sold/held seats for non-admins
      if (seat && !isSold) {
        // Select/deselect immediately (same behavior on mobile and desktop)
        if (seat.boxId) {
          const allBoxSeats = (seatsCacheRef.current['boxes'] || [])
            .filter(s => s.boxId === seat.boxId)
            .map(s => s.id);
          toggleBoxSelection(allBoxSeats);
        } else {
          toggleSeatSelection(seat.id);
        }
        // Mobile: also show seat details modal (non-blocking)
        const { w } = sizeRef.current;
        if (w < 768) {
          const freshSt = useStadiumStore.getState();
          const ss = freshSt.seatMap[seat.id] || seat;
          const viewImg = getSeatViewImage(seat.sectionId);
          const secObj = [...SECTIONS, ...ADA_SECTIONS].find(s => s.id === seat.sectionId);
          setMobileSeatView({
            seat,
            seatState: ss,
            image: viewImg,
            sectionLabel: secObj?.shortLabel || secObj?.label || '',
            isBox: !!seat.boxId,
            boxId: seat.boxId,
          });
        }
      } else if (!seat) {
        // Clicked empty area → dismiss admin info panel
        setAdminSeatInfo(null);
      }
    }
  }, [toCanvas, findSection, findSeatAnywhere, zoomToSection, toggleSeatSelection, toggleBoxSelection, isStaffOrAdmin, toggleAssignableSeatSelection]);

  const onPointerLeave = useCallback(() => {
    setHoveredSection(null);
    setHoveredSeat(null);
    setTooltipData(null);
    dragRef.current.isDown = false;
    dragRef.current.isDragging = false;
    if (canvasRef.current) canvasRef.current.style.cursor = '';
  }, [setHoveredSection, setHoveredSeat]);

  // ── Navigate to a specific seat (from cart chip click) ──
  const navigateToSeat = useCallback((seatId) => {
    const seat = seatMap[seatId];
    if (!seat) return;
    const allSections = [...SECTIONS, ...ADA_SECTIONS];
    const section = allSections.find(s => s.id === seat.sectionId);
    if (!section) return;
    const parentId = section.parentId || section.id;
    const st = useStadiumStore.getState();
    // If already in this section, just pan to seat
    if (st.viewMode === 'section' && st.activeSection === parentId) {
      const { w, h } = sizeRef.current;
      const cam = camRef.current;
      const targetX = w / 2 - seat.x * cam.scale;
      const targetY = h / 2 - seat.y * cam.scale;
      camRef.current = clampCamera({ x: targetX, y: targetY, scale: cam.scale });
      scheduleDraw();
      return;
    }
    // Zoom to section, then pan to seat after animation
    zoomToSection(parentId);
    setTimeout(() => {
      const { w, h } = sizeRef.current;
      const cam = camRef.current;
      const targetX = w / 2 - seat.x * cam.scale;
      const targetY = h / 2 - seat.y * cam.scale;
      camRef.current = clampCamera({ x: targetX, y: targetY, scale: cam.scale });
      scheduleDraw();
    }, 620);
  }, [seatMap, zoomToSection, clampCamera, scheduleDraw]);

  // ── Derived data ──
  const activeSec = useMemo(() => SECTIONS.find(s => s.id === activeSection), [activeSection]);
  const selDetails = useMemo(() => selectedSeats.map(id => seatMap[id]).filter(Boolean), [selectedSeats, seatMap]);
  const total = useMemo(() => selDetails.reduce((s, x) => s + x.price, 0), [selDetails]);
  const cursor = hoveredSection ? 'hovering-section' : hoveredSeat ? 'hovering-seat' : '';

  // ── Checkout: create seat holds, then add to localStorage cart and open sidebar ──
  const handleCheckout = useCallback(async () => {
    if (selDetails.length === 0 || checkoutLoading || (!posMode && hasVendorInCart)) return;
    setCheckoutLoading(true);

    const eventDate = DAY_TO_EVENT_DATE[selectedDayRef.current] || '2026-06-20';
    const dayLabel = selectedDayRef.current === 'sunday' ? 'Sun Jun 21' : 'Sat Jun 20';
    const sessionKey = getStadiumSessionKey();

    // Group seats by API section ID for hold creation
    const boxGroups = {};
    const regularSeats = [];
    selDetails.forEach(seat => {
      if (seat.boxId) {
        if (!boxGroups[seat.boxId]) boxGroups[seat.boxId] = [];
        boxGroups[seat.boxId].push(seat);
      } else {
        regularSeats.push(seat);
      }
    });

    // Group regular seats by their API section ID
    const seatsByApiSection = {};
    regularSeats.forEach(seat => {
      const apiSid = STADIUM_TO_API[seat.sectionId];
      if (!apiSid) return;
      if (!seatsByApiSection[apiSid]) seatsByApiSection[apiSid] = [];
      seatsByApiSection[apiSid].push(seat);
    });

    // Create backend holds for each section's seats
    const holdIds = [];
    const holdMeta = [];
    let holdExpiresAt = null;
    let holdFailed = false;
    const holdErrors = [];

    // Hold regular seats (grouped by API section)
    for (const [apiSid, seats] of Object.entries(seatsByApiSection)) {
      try {
        const holdResult = await api.createSeatHold(
          apiSid,
          eventDate,
          seats.length,
          sessionKey,
          seats.map(s => ({ row: s.row, number: s.seatNumber }))
        );
        if (holdResult?.success && holdResult.data?.hold_id) {
          holdIds.push(holdResult.data.hold_id);
          holdMeta.push({
            hold_id: holdResult.data.hold_id,
            section_id: apiSid,
            seats: seats.map(s => ({ row: s.row, seat: s.seatNumber })),
          });
          if (holdResult.data.expires_at) holdExpiresAt = holdResult.data.expires_at;
        } else {
          const errMsg = holdResult?.data?.error || holdResult?.error || 'Unknown error';
          console.warn('[Stadium] Seat hold failed for', apiSid, errMsg);
          holdErrors.push(errMsg);
          holdFailed = true;
        }
      } catch (err) {
        const errMsg = err?.response?.data?.error || err?.message || 'Network error';
        console.warn('[Stadium] Seat hold error for', apiSid, errMsg);
        holdErrors.push(errMsg);
        holdFailed = true;
      }
    }

    // Hold box seats
    for (const [boxId, seats] of Object.entries(boxGroups)) {
      const apiBoxSid = `box-${boxId.replace('B', '')}`;
      try {
        const holdResult = await api.createSeatHold(
          apiBoxSid,
          eventDate,
          seats.length,
          sessionKey,
          seats.map(s => ({ row: s.row, number: s.seatNumber }))
        );
        if (holdResult?.success && holdResult.data?.hold_id) {
          holdIds.push(holdResult.data.hold_id);
          holdMeta.push({
            hold_id: holdResult.data.hold_id,
            section_id: apiBoxSid,
            seats: seats.map(s => ({ row: s.row, seat: s.seatNumber })),
          });
          if (holdResult.data.expires_at) holdExpiresAt = holdResult.data.expires_at;
        } else {
          const errMsg = holdResult?.data?.error || holdResult?.error || 'Unknown error';
          console.warn('[Stadium] Box hold failed for', apiBoxSid, errMsg);
          holdErrors.push(errMsg);
          holdFailed = true;
        }
      } catch (err) {
        const errMsg = err?.response?.data?.error || err?.message || 'Network error';
        console.warn('[Stadium] Box hold error for', apiBoxSid, errMsg);
        holdErrors.push(errMsg);
        holdFailed = true;
      }
    }

    // All-or-nothing hold policy: if any hold fails, release partial holds and abort.
    if (holdFailed) {
      if (holdIds.length > 0) {
        await Promise.allSettled(holdIds.map((id) => api.releaseSeatHold(id)));
      }
      const uniqueErrors = [...new Set(holdErrors)];
      const errorDisplay = uniqueErrors.length > 0
        ? uniqueErrors.join('\n')
        : 'One or more selected seats are currently being purchased by another customer. Please choose different seats.';
      alert(errorDisplay);
      setCheckoutLoading(false);
      return;
    }

    // Build cart items — filter out vendor items (mutual exclusivity, matches amphitheater-tickets)
    const existingCart = JSON.parse(localStorage.getItem('cart') || '{"items":[],"total":0}');
    const items = (existingCart.items || []).filter(item =>
      item.type !== 'vendor-booth' && item.type !== 'vendor' && item.type !== 'vendor-attendee'
    );

    // Build seatDetails for cart (matching amphitheater-tickets format)
    const allSeatDetails = selDetails.map(seat => {
      const sec = [...SECTIONS, ...ADA_SECTIONS].find(s => s.id === seat.sectionId);
      const apiSid = seat.boxId
        ? `box-${seat.boxId.replace('B', '')}`
        : (STADIUM_TO_API[seat.sectionId] || seat.sectionId);
      const box = seat.boxId ? BOX_DEFS.find(b => b.id === seat.boxId) : null;
      return {
        id: seat.id,
        sectionId: apiSid,
        section: box ? box.label : (sec?.label || sec?.shortLabel || ''),
        row: seat.row,
        number: seat.seatNumber,
        price: seat.price,
      };
    });

    const totalPrice = Math.round(selDetails.reduce((s, x) => s + x.price, 0) * 100) / 100;
    const seatsInfo = selDetails.map(s => `Row ${s.row}, Seat ${s.seatNumber}`).join('; ');

    const cartItem = {
      id: `amphitheater-${Date.now()}`,
      ticket_type_id: `amphitheater-${selectedDayRef.current}`,
      type: 'amphitheater',
      name: `Pacific Amphitheatre - ${dayLabel === 'Sat Jun 20' ? 'Saturday, June 20' : 'Sunday, June 21'}`,
      section: [...new Set(selDetails.map(s => {
        const box = s.boxId ? BOX_DEFS.find(b => b.id === s.boxId) : null;
        if (box) return box.label;
        const sec = [...SECTIONS, ...ADA_SECTIONS].find(x => x.id === s.sectionId);
        return sec?.label || sec?.shortLabel || '';
      }))].join(', '),
      seats: seatsInfo,
      seatDetails: allSeatDetails,
      day: selectedDayRef.current,
      eventDate,
      quantity: selDetails.length,
      price: totalPrice / selDetails.length,
      total: totalPrice,
      includesFestival: true,
      holdIds: holdIds.length > 0 ? holdIds : null,
      holdMeta,
      sessionKey: null,
      expiresAt: holdExpiresAt,
    };

    // POS mode: hand seat-hold payload back to parent instead of pushing into
    // website cart/localStorage flow.
    if (posMode && typeof onPosSeatHoldCreated === 'function') {
      onPosSeatHoldCreated({
        day: selectedDayRef.current,
        eventDate,
        totalCents: Math.round(totalPrice * 100),
        holdIds,
        holdMeta,
        seatDetails: allSeatDetails,
        expiresAt: holdExpiresAt,
      });
      clearSelection();
      setCheckoutLoading(false);
      return;
    }

    items.push(cartItem);
    const newTotal = items.reduce((s, i) => s + (i.total || i.price * i.quantity), 0);
    const cartData = { items, total: newTotal };
    localStorage.setItem('cart', JSON.stringify(cartData));
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cartData }));
    window.dispatchEvent(new CustomEvent('openCart'));

    // ── Meta Pixel: AddToCart (matches amphitheater-tickets) ──
    trackAddToCart({
      contentName: cartItem.name,
      contentIds: [cartItem.id],
      value: totalPrice,
      currency: 'USD',
      numItems: selDetails.length,
    });

    // Clear selection and open cart
    clearSelection();
    setIsCartOpen(true);
    document.body.style.overflow = 'hidden';
    setCheckoutLoading(false);

    if (holdExpiresAt) {
      const mins = Math.round((new Date(holdExpiresAt) - new Date()) / 60000);
      console.log(`[Stadium] Seats held for ${mins} minutes. Complete checkout before they expire!`);
    }
  }, [selDetails, clearSelection, checkoutLoading, hasVendorInCart, posMode, onPosSeatHoldCreated]);

  const refreshSoldSeats = useCallback(async () => {
    const eventDate = DAY_TO_EVENT_DATE[selectedDayRef.current] || DAY_TO_EVENT_DATE.saturday;
    try {
      const res = await api.getAmphitheaterSeats(eventDate, { withAuth: !!api.getToken() });
      if (res?.success && res?.data) {
        applySoldData(res);
      }
      await loadSamAssignableSeats(eventDate);
    } catch (_) {
      // best-effort refresh only
    }
  }, [applySoldData, loadSamAssignableSeats]);

  const adminSeatTargets = useMemo(() => {
    if (!selDetails || !selDetails.length) return [];
    const eventDate = DAY_TO_EVENT_DATE[selectedDayRef.current] || DAY_TO_EVENT_DATE.saturday;
    return selDetails.map((seat) => {
      const apiSection = seat.boxId ? `box-${String(seat.boxId || '').replace('B', '')}` : (STADIUM_TO_API[seat.sectionId] || seat.sectionId || '');
      const sectionLabel = seat.boxId
        ? `Premium ${BOX_DEFS.find((b) => b.id === seat.boxId)?.label || seat.boxId}`
        : ([...SECTIONS, ...ADA_SECTIONS].find((s) => s.id === seat.sectionId)?.shortLabel || seat.sectionId || '');
      return {
        seat_section: apiSection,
        section_label: sectionLabel,
        seat_row: seat.row,
        seat_number: seat.seatNumber,
        event_date: eventDate,
      };
    });
  }, [selDetails]);

  const openAssignSeatModal = useCallback(() => {
    if (!isAdminAssignMode) return;
    if (!selDetails.length) return;
    setAssignError('');
    setShowAssignModal(true);
  }, [isAdminAssignMode, selDetails]);

  const submitAdminSeatAssign = useCallback(async (payload) => {
    if (!adminSeatTargets.length) {
      setAssignError('Select at least one unsold seat before assigning.');
      return;
    }

    setAssignLoading(true);
    setAssignError('');
    try {
      let successCount = 0;

      for (const seatTarget of adminSeatTargets) {
        const seatPayload = {
          ...payload,
          assignment_type: 'amp_seat',
          seat_section: seatTarget.seat_section,
          seat_row: seatTarget.seat_row,
          seat_number: seatTarget.seat_number,
          event_date: seatTarget.event_date,
        };
        const res = await api.assignAdminUnsoldSeatOrPass(seatPayload);
        if (!res?.success) {
          const errMsg = res?.error?.message || 'Failed to assign seat';
          const prefix = successCount > 0 ? `${successCount} seat(s) assigned. ` : '';
          setAssignError(`${prefix}${errMsg}`);
          return;
        }
        successCount += 1;
      }

      window.alert(
        successCount === 1
          ? 'Seat assigned successfully.'
          : `${successCount} seats assigned successfully.`
      );
      setShowAssignModal(false);
      clearSelection();
      await refreshSoldSeats();
    } catch (e) {
      setAssignError(e.message || 'Failed to assign seat');
    } finally {
      setAssignLoading(false);
    }
  }, [adminSeatTargets, clearSelection, refreshSoldSeats]);

  const submitSamSeatAssign = useCallback(async (payload) => {
    if (!samAssignSeatTargets.length) {
      setSamAssignError('Select at least one reserved seat before assigning.');
      return;
    }

    setSamAssignLoading(true);
    setSamAssignError('');
    try {
      let successCount = 0;
      let firstTicketId = '';
      for (const seatTarget of samAssignSeatTargets) {
        if (!seatTarget?.ticket_id && !seatTarget?.amphitheater_ticket_id) {
          setSamAssignError('One selected reserved seat is missing ticket context. Refresh and try again.');
          return;
        }

        const poolStyle = getAssignablePoolStyle(seatTarget?.pool_key);
        const sourcePoolLabel = seatTarget?.pool_label || poolStyle.label || 'Reserved pool';
        const sourcePoolAccount = seatTarget?.pool_account_email || '';
        const res = await api.createAdminSeatAssignment({
          source: 'other',
          ticket_id: seatTarget.ticket_id || '',
          amphitheater_ticket_id: seatTarget.amphitheater_ticket_id || '',
          attendee_first_name: payload.first_name,
          attendee_last_name: payload.last_name,
          attendee_email: payload.email,
          attendee_phone: '',
          attendee_organization: '',
          notes: `Stadium admin assignment from ${sourcePoolLabel}${sourcePoolAccount ? ` (${sourcePoolAccount})` : ''}`,
        });

        if (!res?.success) {
          const errMsg = res?.error?.message || `Failed to assign reserved seat from ${sourcePoolLabel}`;
          const prefix = successCount > 0 ? `${successCount} seat(s) assigned. ` : '';
          setSamAssignError(`${prefix}${errMsg}`);
          return;
        }
        successCount += 1;
        if (!firstTicketId) {
          firstTicketId = res?.data?.ticket_id || seatTarget.ticket_id || '';
        }
      }

      let emailWarning = '';
      if (payload.send_email) {
        const emailRes = await api.sendAdminOwnerTicketEmail({
          ticketId: firstTicketId,
          toEmail: payload.email,
        });
        if (!emailRes?.success) {
          emailWarning = ` Assignment succeeded, but ticket email could not be sent to ${payload.email}.`;
        }
      }

      window.alert(
        successCount === 1
          ? `Seat assigned successfully.${emailWarning}`
          : `${successCount} seats assigned successfully.${emailWarning}`
      );
      setShowSamAssignModal(false);
      setSamAssignSeatTargets([]);
      clearAssignableSeatSelection();
      await refreshSoldSeats();
    } catch (e) {
      setSamAssignError(e.message || 'Failed to assign reserved seats');
    } finally {
      setSamAssignLoading(false);
    }
  }, [clearAssignableSeatSelection, refreshSoldSeats, samAssignSeatTargets]);

  const handleCloseCart = useCallback(() => {
    setIsCartOpen(false);
    document.body.style.overflow = '';
  }, []);

  // ── Render (container + canvas always mounted so resize works) ──
  return (
    <>
    <div className="stadium-page" ref={containerRef}>
      <canvas
        ref={canvasRef}
        className={`stadium-canvas ${cursor}`}
        onPointerMove={onPointerMove}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
      />

      {/* ── Header ── */}
      <header className="stadium-header">
        <div className="stadium-header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <Link to="/" className="stadium-header-logo" title="OC MENA Festival Home">
              <img src={getCmsImagePath('logo', '/logo.webp')} alt="OC MENA Festival" className="stadium-header-logo-img" />
            </Link>
            <div className="stadium-header-venue-text">
              <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>Pacific Amphitheatre</div>
              <div style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>Concert Tickets · Costa Mesa, CA · June 20-21, 2026</div>
            </div>
            {hideDaySwitcher && (
              <div className="stadium-day-label">
                <div className="stadium-day-label-day">{selectedDay === 'sunday' ? 'Sunday, June 21' : 'Saturday, June 20'}</div>
                <div className="stadium-day-label-artists">{ampArtistNames[selectedDay]}</div>
              </div>
            )}
          </div>
          {!hideDaySwitcher && <div className="stadium-day-buttons">
            <button
              className={`stadium-day-btn ${selectedDay === 'saturday' ? 'active' : ''}`}
              disabled={!dayStatus.saturday.enabled}
              onClick={() => {
                if (selectedDay !== 'saturday' && dayStatus.saturday.enabled) {
                  clearSelection();
                  setTooltipData(null);
                  if (viewMode === 'section') zoomOut();
                  setSelectedDay('saturday');
                }
              }}
            >
              Saturday, June 20
              <span style={{ display: 'block', fontSize: 12, fontWeight: 500, opacity: 0.9 }}>{ampArtistNames.saturday || 'Lineup coming soon'}</span>
            </button>
            <button
              className={`stadium-day-btn ${selectedDay === 'sunday' ? 'active' : ''} ${!dayStatus.sunday.enabled ? 'disabled' : ''}`}
              disabled={!dayStatus.sunday.enabled}
              onClick={() => {
                if (selectedDay !== 'sunday' && dayStatus.sunday.enabled) {
                  clearSelection();
                  setTooltipData(null);
                  if (viewMode === 'section') zoomOut();
                  setSelectedDay('sunday');
                }
              }}
              style={!dayStatus.sunday.enabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              Sunday, June 21
              {dayStatus.sunday.enabled
                ? <span style={{ display: 'block', fontSize: 12, fontWeight: 500, opacity: 0.9 }}>{ampArtistNames.sunday || 'Lineup coming soon'}</span>
                : <span style={{ display: 'block', fontSize: 12, fontWeight: 500, opacity: 0.9 }}>Coming Soon</span>
              }
            </button>
          </div>}
          <div className="stadium-header-actions">
            <Link
              to={isAuthenticated ? '/dashboard' : '/login'}
              className="stadium-header-icon-btn"
              aria-label="User Account"
              title={isAuthenticated ? 'Dashboard' : 'Account'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="20" viewBox="0 0 18 20" fill="none">
                <path d="M5 5C5 4.50754 5.097 4.01991 5.28545 3.56494C5.47391 3.10997 5.75013 2.69657 6.09835 2.34835C6.44657 2.00013 6.85997 1.72391 7.31494 1.53545C7.76991 1.347 8.25754 1.25 8.75 1.25C9.24246 1.25 9.73009 1.347 10.1851 1.53545C10.64 1.72391 11.0534 2.00013 11.4017 2.34835C11.7499 2.69657 12.0261 3.10997 12.2145 3.56494C12.403 4.01991 12.5 4.50754 12.5 5C12.5 5.49246 12.403 5.98009 12.2145 6.43506C12.0261 6.89003 11.7499 7.30343 11.4017 7.65165C11.0534 7.99987 10.64 8.27609 10.1851 8.46455C9.73009 8.653 9.24246 8.75 8.75 8.75C8.25754 8.75 7.76991 8.653 7.31494 8.46455C6.85997 8.27609 6.44657 7.99987 6.09835 7.65165C5.75013 7.30343 5.47391 6.89003 5.28545 6.43506C5.097 5.98009 5 5.49246 5 5ZM13.75 5C13.75 3.67392 13.2232 2.40215 12.2855 1.46447C11.3479 0.526784 10.0761 0 8.75 0C7.42392 0 6.15215 0.526784 5.21447 1.46447C4.27678 2.40215 3.75 3.67392 3.75 5C3.75 6.32608 4.27678 7.59785 5.21447 8.53553C6.15215 9.47322 7.42392 10 8.75 10C10.0761 10 11.3479 9.47322 12.2855 8.53553C13.2232 7.59785 13.75 6.32608 13.75 5ZM1.25 18.75C1.25 15.6445 3.76953 13.125 6.875 13.125H10.625C13.7305 13.125 16.25 15.6445 16.25 18.75V19.375C16.25 19.7188 16.5312 20 16.875 20C17.2188 20 17.5 19.7188 17.5 19.375V18.75C17.5 14.9531 14.4219 11.875 10.625 11.875H6.875C3.07812 11.875 0 14.9531 0 18.75V19.375C0 19.7188 0.28125 20 0.625 20C0.96875 20 1.25 19.7188 1.25 19.375V18.75Z" fill="currentColor" />
              </svg>
            </Link>
            <button className="stadium-header-icon-btn" aria-label="Shopping Cart" onClick={() => { setIsCartOpen(true); document.body.style.overflow = 'hidden'; }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 3.75V6.25H12.5V3.75C12.5 2.37109 11.3789 1.25 10 1.25C8.62109 1.25 7.5 2.37109 7.5 3.75ZM6.25 7.5H3.125C2.78125 7.5 2.5 7.78125 2.5 8.125V16.25C2.5 17.6289 3.62109 18.75 5 18.75H15C16.3789 18.75 17.5 17.6289 17.5 16.25V8.125C17.5 7.78125 17.2188 7.5 16.875 7.5H13.75V10.625C13.75 10.9688 13.4688 11.25 13.125 11.25C12.7812 11.25 12.5 10.9688 12.5 10.625V7.5H7.5V10.625C7.5 10.9688 7.21875 11.25 6.875 11.25C6.53125 11.25 6.25 10.9688 6.25 10.625V7.5ZM6.25 6.25V3.75C6.25 1.67969 7.92969 0 10 0C12.0703 0 13.75 1.67969 13.75 3.75V6.25H16.875C17.9102 6.25 18.75 7.08984 18.75 8.125V16.25C18.75 18.3203 17.0703 20 15 20H5C2.92969 20 1.25 18.3203 1.25 16.25V8.125C1.25 7.08984 2.08984 6.25 3.125 6.25H6.25Z" fill="currentColor" />
              </svg>
              {cartItemCount > 0 && <span className="stadium-cart-badge">{cartItemCount}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Admin-only: toggle off the per-section "sold out" greying so admins can
          open fully-sold sections (both Saturday & Sunday) and inspect seat
          numbers, prices, and buyer info. */}
      {isStaffOrAdmin && (
        <button
          type="button"
          className={`stadium-admin-soldout-toggle${adminBypassSoldOut ? ' is-on' : ''}`}
          onClick={() => setAdminBypassSoldOut(!adminBypassSoldOut)}
          title="Admin only: reveal sold-out sections to inspect seat numbers, prices and buyers"
          aria-pressed={adminBypassSoldOut}
        >
          <span className="stadium-admin-soldout-dot" />
          {adminBypassSoldOut ? 'Showing All Seats · Admin' : 'Reveal Sold-Out · Admin'}
        </button>
      )}

      {(loading || loadingExit) && (
        <div className={`stadium-loading-screen${loadingExit ? ' exit' : ''}`}>
          <img src={getCmsImagePath('logo', '/logo.webp')} alt="OC MENA Festival" className="stadium-loading-logo" />

          <div className="stadium-loading-ring-wrap">
            <svg className="stadium-loading-ring" viewBox="0 0 120 120">
              <circle className="stadium-loading-ring-bg" cx="60" cy="60" r="52" />
              <circle className="stadium-loading-ring-fg" cx="60" cy="60" r="52"
                strokeDasharray={2 * Math.PI * 52}
                strokeDashoffset={2 * Math.PI * 52 * (1 - loadingProgress / 100)}
              />
            </svg>
            <span className="stadium-loading-pct">{Math.round(loadingProgress)}%</span>
          </div>

          <div className="stadium-loading-title">Loading seats...</div>
          <div className="stadium-loading-sub">
            {selectedDay === 'sunday'
              ? `Sunday, June 21 \u2022 ${ampArtistNames.sunday || 'Lineup coming soon'}`
              : `Saturday, June 20 \u2022 ${ampArtistNames.saturday || 'Lineup coming soon'}`}
          </div>
        </div>
      )}

      {/* Best-experience message — shown on top of stadium in overview */}
      {!loading && viewMode === 'overview' && (
        <div className="stadium-desktop-notice">
          For the best seat selection experience, please view this page on a desktop or larger screen.
        </div>
      )}

      {/* Floating back button (section view only) */}
      {viewMode === 'section' && (
        <button className="stadium-back-btn floating" onClick={() => { setTooltipData(null); setAdminSeatInfo(null); zoomOut(); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          All Sections
        </button>
      )}

      {/* Section info (zoomed) */}
      {viewMode === 'section' && activeSec && (
        <div className="section-info-panel" style={{ opacity: panelOpacity }}>
          <div className="section-info-name">{activeSec.label}</div>
          <div className="section-info-price">
            From <span>${activeSec.priceRange[0].toLocaleString()}</span> – <span>${activeSec.priceRange[1].toLocaleString()}</span>
          </div>
          <div className="section-info-legend">
            {(() => {
              const ds = window.__stadiumDebugStyle || {};
              const sid = activeSec.id;
              const availColor = (ds[sid + '_seat'] || activeSec.color) + (ds[sid + '_seatOp'] || '99');
              const selColor = ds.selected || COLORS.selected;
              const soldColor = ds[sid + '_soldSeat'] || ds.soldSeat || 'rgba(255,255,255,0.05)';
              const adaColor = (ds['ada_seatFill'] || COLORS.ada) + (ds['ada_seatOp'] || '90');
              return (<>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: availColor }} />
                  Available
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: selColor }} />
                  Selected
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: soldColor, border: '1px solid rgba(255,255,255,0.15)' }} />
                  Sold
                </div>
                {isStaffOrAdmin && (assignablePoolOptions.length > 0 ? assignablePoolOptions : [
                  { key: 'shared', label: 'Shared Pool', color: '#22c55e' },
                ]).map((pool) => (
                  <div key={pool.key} className="legend-item">
                    <div className="legend-dot" style={{ background: pool.color || '#22c55e' }} />
                    {pool.label || pool.key}
                  </div>
                ))}
                {ADA_SECTIONS.some(a => a.parentId === activeSec?.id) && (
                  <div className="legend-item">
                    <div className="legend-dot" style={{ background: adaColor }} />
                    ADA
                  </div>
                )}
              </>);
            })()}
          </div>
        </div>
      )}


      {/* Zoom controls (+/- buttons) */}
      <div className="stadium-zoom-controls">
        {viewMode === 'section' && (
          <button className="zoom-btn zoom-back-btn" onClick={() => { setTooltipData(null); setAdminSeatInfo(null); zoomOut(); }}
            title="Back to all sections">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        <button className="zoom-btn" onClick={() => handleZoomButton('in')}>+</button>
        <button className="zoom-btn" onClick={() => handleZoomButton('out')}>−</button>
      </div>

      {/* Instruction (overview) */}
      {viewMode === 'overview' && !isMobile && (
        <div className="zoom-instruction">Tap a section to view seats</div>
      )}
      {viewMode === 'overview' && isMobile && (
        <div className="zoom-instruction">Tap a section to view seats</div>
      )}

      {/* Tooltip (desktop only — hidden on mobile via CSS) */}
      {tooltipData && (
        <div className={`stadium-tooltip visible ${tooltipData.viewImage ? 'has-image' : ''} ${tooltipData.isSold ? 'is-sold' : ''}`}
          style={{ left: tooltipData.x, top: tooltipData.y }}>
          {tooltipData.viewImage && (
            <img loading="lazy" src={tooltipData.viewImage} alt="View from seat" className="tooltip-view-img" />
          )}
          <div className="tooltip-text-row">
            {tooltipData.label}
            <span className={`tooltip-price ${tooltipData.isSold ? 'sold' : ''}`}>{tooltipData.price}</span>
            {tooltipData.seats != null && (
              <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 11 }}>{tooltipData.seats} seats</span>
            )}
          </div>
          {tooltipData.viewImage && (
            <div className="tooltip-view-label">View from this section</div>
          )}
          {tooltipData.buyerInfo && (
            <div style={{ marginTop: 6, padding: '6px 8px', background: 'rgba(99,102,241,0.15)', borderRadius: 6, fontSize: 11, lineHeight: 1.5, borderLeft: '3px solid #6366f1' }}>
              <div style={{ fontWeight: 700, color: '#a5b4fc', marginBottom: 2 }}>
                Purchased by {tooltipData.buyerInfo.isComp && <span style={{ background: '#22c55e', color: '#fff', padding: '1px 5px', borderRadius: 4, fontSize: 9, fontWeight: 800, marginLeft: 4 }}>COMP</span>}
              </div>
              {tooltipData.buyerInfo.name && <div style={{ color: '#e2e8f0' }}>{tooltipData.buyerInfo.name}</div>}
              {tooltipData.buyerInfo.email && <div style={{ color: '#cbd5e1' }}>{tooltipData.buyerInfo.email}</div>}
              {tooltipData.buyerInfo.phone && <div style={{ color: '#cbd5e1' }}>{tooltipData.buyerInfo.phone}</div>}
              {tooltipData.buyerInfo.paidCents > 0 && <div style={{ color: '#86efac', fontWeight: 600 }}>Paid for seat: ${(tooltipData.buyerInfo.paidCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>}
              {tooltipData.buyerInfo.purchasedAt && <div style={{ color: '#94a3b8', fontSize: 10 }}>Date: {new Date(tooltipData.buyerInfo.purchasedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>}
              {tooltipData.buyerInfo.order && <div style={{ color: '#94a3b8', fontSize: 10 }}>Order: {tooltipData.buyerInfo.order}</div>}
            </div>
          )}
          {tooltipData.isAssignable && (
            <div style={{ marginTop: 6, padding: '6px 8px', background: 'rgba(15,23,42,0.7)', borderRadius: 6, fontSize: 11, lineHeight: 1.5, borderLeft: `3px solid ${tooltipData.assignablePoolColor || '#22c55e'}`, color: '#e2e8f0' }}>
              <div style={{ fontWeight: 700 }}>{tooltipData.assignablePoolLabel || 'Reserved seat pool'}</div>
              {tooltipData.assignablePoolAccountEmail ? <div style={{ color: '#cbd5e1' }}>{tooltipData.assignablePoolAccountEmail}</div> : null}
              <div style={{ color: '#a7f3d0' }}>
                Staff/Admin only · {tooltipData.isAssignableSelected ? 'Selected for assignment' : 'Click to select'}
              </div>
            </div>
          )}
        </div>
      )}

      {isStaffOrAdmin && selectedAssignableTargets.length > 0 && (
        <div style={{
          position: 'fixed',
          right: 16,
          bottom: 92,
          zIndex: 1000,
          background: 'rgba(15,23,42,0.92)',
          color: '#e2e8f0',
          border: '1px solid rgba(14,165,233,0.45)',
          borderRadius: 12,
          padding: '10px 12px',
          minWidth: 250,
          boxShadow: '0 14px 30px rgba(0,0,0,0.35)',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Reserved Pool Seats</div>
          <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 8 }}>{selectedAssignableTargets.length} selected from private/shared pools.</div>
          {actorEmail ? <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8 }}>Signed in as {actorEmail}</div> : null}
          {assignablePoolOptions.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {assignablePoolOptions.map((pool) => (
                <span key={pool.key} style={{
                  fontSize: 10,
                  padding: '2px 6px',
                  borderRadius: 999,
                  border: '1px solid rgba(148,163,184,0.4)',
                  background: `${pool.color || '#22c55e'}22`,
                }}>{pool.label || pool.key}</span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                setSamAssignSeatTargets(selectedAssignableTargets);
                setShowSamAssignModal(true);
              }}
              style={{ padding: '0.38rem 0.75rem', borderRadius: 8, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
            >
              Assign selected
            </button>
            <button
              onClick={clearAssignableSeatSelection}
              style={{ padding: '0.38rem 0.75rem', borderRadius: 8, border: '1px solid #475569', background: 'transparent', color: '#cbd5e1', cursor: 'pointer' }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Admin: Persistent buyer info panel (click on sold seat) */}
      {adminSeatInfo && (
        <div style={{
          position: 'fixed', left: Math.min(adminSeatInfo.x + 12, window.innerWidth - 320), top: Math.min(adminSeatInfo.y - 20, window.innerHeight - 280),
          zIndex: 10000, background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', borderRadius: 12,
          padding: '14px 16px', minWidth: 260, maxWidth: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,102,241,0.3)',
          color: '#e2e8f0', fontSize: 13, lineHeight: 1.6, pointerEvents: 'auto', userSelect: 'text', cursor: 'text',
        }}
          onPointerDown={e => e.stopPropagation()}
          onPointerUp={e => e.stopPropagation()}
          onPointerMove={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{adminSeatInfo.label}</div>
            <button onClick={() => setAdminSeatInfo(null)} style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', color: '#94a3b8', cursor: 'pointer',
              borderRadius: 6, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            }}>&times;</button>
          </div>
          {adminSeatInfo.buyerInfo ? (
            <div style={{ borderLeft: '3px solid #6366f1', paddingLeft: 10 }}>
              <div style={{ fontWeight: 600, color: '#a5b4fc', marginBottom: 4 }}>
                Purchased by {adminSeatInfo.buyerInfo.isComp && <span style={{ background: '#22c55e', color: '#fff', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 800, marginLeft: 6 }}>COMP</span>}
              </div>
              {adminSeatInfo.buyerInfo.name && <div style={{ color: '#e2e8f0' }}>{adminSeatInfo.buyerInfo.name}</div>}
              {adminSeatInfo.buyerInfo.email && <div style={{ color: '#cbd5e1' }}>{adminSeatInfo.buyerInfo.email}</div>}
              {adminSeatInfo.buyerInfo.phone && <div style={{ color: '#cbd5e1' }}>{adminSeatInfo.buyerInfo.phone}</div>}
              {adminSeatInfo.buyerInfo.paidCents > 0 && <div style={{ color: '#86efac', fontWeight: 600, marginTop: 4 }}>Paid for seat: ${(adminSeatInfo.buyerInfo.paidCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>}
              {adminSeatInfo.buyerInfo.purchasedAt && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>Date: {new Date(adminSeatInfo.buyerInfo.purchasedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>}
              {adminSeatInfo.buyerInfo.order && <div style={{ color: '#94a3b8', fontSize: 11 }}>Order: {adminSeatInfo.buyerInfo.order}</div>}
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontStyle: 'italic' }}>No buyer details available for this seat</div>
          )}
        </div>
      )}

      {/* Mobile Seat View Modal (non-blocking — map stays interactive behind it) */}
      {mobileSeatView && (
        <div className="mobile-seat-modal-overlay">
          <div className="mobile-seat-modal">
            {mobileSeatView.image && (
              <img loading="lazy" src={mobileSeatView.image} alt="View from seat" className="mobile-seat-img" />
            )}
            <div className="mobile-seat-info">
              <div className="mobile-seat-title">
                {mobileSeatView.isBox
                  ? `Premium ${BOX_DEFS.find(b => b.id === mobileSeatView.boxId)?.label || mobileSeatView.boxId}`
                  : `${mobileSeatView.sectionLabel} · Row ${mobileSeatView.seat.row} · Seat ${mobileSeatView.seat.seatNumber}`
                }
              </div>
              <div className="mobile-seat-price">${mobileSeatView.isBox
                ? (() => {
                    const allBoxSeats = (seatsCacheRef.current['boxes'] || []).filter(s => s.boxId === mobileSeatView.boxId);
                    return Math.round(allBoxSeats.reduce((sum, s) => sum + (seatMap[s.id]?.price || s.price), 0)).toLocaleString();
                  })()
                : (mobileSeatView.seatState?.price || mobileSeatView.seat.price).toLocaleString()
              }</div>
              <div className="mobile-seat-view-label">View from this section</div>
            </div>
            <div className="mobile-seat-actions">
              <button className="mobile-seat-select-btn mobile-seat-remove" onClick={() => {
                const seat = mobileSeatView.seat;
                if (seat.boxId) {
                  const allBoxSeats = (seatsCacheRef.current['boxes'] || [])
                    .filter(s => s.boxId === seat.boxId).map(s => s.id);
                  toggleBoxSelection(allBoxSeats);
                } else {
                  toggleSeatSelection(seat.id);
                }
                setMobileSeatView(null);
              }}>
                Cancel
              </button>
              <button className="mobile-seat-close-btn" onClick={() => setMobileSeatView(null)}>Select Seat</button>
            </div>
          </div>
        </div>
      )}

      {/* Cart overlay removed — users can now select multiple seats without map locking */}

      {/* Cart */}
      <div className={`stadium-cart-panel ${selectedSeats.length > 0 ? 'visible' : ''}`}>
        <div className="stadium-cart-header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span className="stadium-cart-title">Your Seats</span>
            <span className="stadium-cart-count">{selectedSeats.length}</span>
          </div>
          {selectedSeats.length > 0 && (
            <button className="stadium-cart-clear-btn" onClick={clearSelection}>Clear all</button>
          )}
        </div>
        <div className="stadium-cart-seats-list">
          {(() => {
            // Group box seats by boxId, show one chip per box
            const boxGroups = {};
            const nonBoxSeats = [];
            selDetails.forEach(seat => {
              if (seat.boxId) {
                if (!boxGroups[seat.boxId]) boxGroups[seat.boxId] = [];
                boxGroups[seat.boxId].push(seat);
              } else {
                nonBoxSeats.push(seat);
              }
            });
            return (
              <>
                {Object.entries(boxGroups).map(([boxId, seats]) => (
                  <div key={boxId} className="stadium-cart-seat-chip" onClick={() => navigateToSeat(seats[0].id)}>
                    Premium {BOX_DEFS.find(b => b.id === boxId)?.label || boxId} · {seats.length} seats
                    <span className="remove" onClick={(e) => { e.stopPropagation(); toggleBoxSelection(seats.map(s => s.id)); }}>×</span>
                  </div>
                ))}
                {nonBoxSeats.map(seat => (
                  <div key={seat.id} className="stadium-cart-seat-chip" onClick={() => navigateToSeat(seat.id)}>
                    {[...SECTIONS, ...ADA_SECTIONS].find(s => s.id === seat.sectionId)?.shortLabel || ''} · Row {seat.row} · #{seat.seatNumber}
                    <span className="remove" onClick={(e) => { e.stopPropagation(); toggleSeatSelection(seat.id); }}>×</span>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
        <div className="stadium-cart-footer">
          <div className="stadium-cart-total"><small>Total </small>${Math.round(total).toLocaleString()}</div>
          <button
            className="stadium-cart-checkout-btn"
            onClick={isAdminAssignMode ? openAssignSeatModal : handleCheckout}
            disabled={isAdminAssignMode ? assignLoading : (checkoutLoading || (!posMode && hasVendorInCart))}
            style={isAdminAssignMode
              ? (assignLoading ? { opacity: 0.6, cursor: 'wait' } : {})
              : ((checkoutLoading || (!posMode && hasVendorInCart)) ? { opacity: 0.6, cursor: checkoutLoading ? 'wait' : 'not-allowed' } : {})}
          >
            {isAdminAssignMode
              ? (assignLoading ? 'Assigning…' : (selDetails.length > 1 ? 'Add Assignments' : 'Add Assignment'))
              : (checkoutLoading ? 'Holding seats…' : 'Checkout →')}
          </button>
          {!posMode && hasVendorInCart && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: '#f59e0b', textAlign: 'center', lineHeight: 1.4 }}>
              You have a vendor product in your cart. Please complete or remove your vendor booth order first.
            </div>
          )}
        </div>
      </div>

    </div>

    {/* Cart Sidebar — website flow only */}
    {!posMode && <CartSidebar isOpen={isCartOpen} onClose={handleCloseCart} />}
    <AdminUnsoldAssignModal
      open={showAssignModal}
      onClose={() => { if (!assignLoading) { setShowAssignModal(false); setAssignError(''); } }}
      onSubmit={submitAdminSeatAssign}
      loading={assignLoading}
      error={assignError}
      assignmentType="amp_seat"
      seat={adminSeatTargets[0] || null}
      seatCount={adminSeatTargets.length}
      defaultEmail={assignPrefill.email}
      defaultFirstName={assignPrefill.first}
      defaultLastName={assignPrefill.last}
      sendEmailOnAssign={assignPrefill.sendEmail}
    />
    <AdminUnsoldAssignModal
      open={showSamAssignModal}
      onClose={() => {
        if (!samAssignLoading) {
          setShowSamAssignModal(false);
          setSamAssignSeatTargets([]);
          setSamAssignError('');
        }
      }}
      onSubmit={submitSamSeatAssign}
      loading={samAssignLoading}
      error={samAssignError}
      assignmentType="amp_seat"
      seat={samAssignSeatTargets[0] || null}
      seatCount={samAssignSeatTargets.length || 1}
    />
  </>
  );
}
